const en = {
  // Common words and phrases
  common: {
    back: "Back",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    export: "Export",
    currency: "Currency",
    country: "Country",
    download: "Download",
    loading: "Loading...",
    saving: "Saving...",
    save: "Save",
    save_changes: "Save Changes",
    apply: "Apply",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    add: "Add",
    name: "Name",
    email: "Email",
    password: "Password",
    confirm: "Confirm",
    submit: "Submit",
    error: "Error",
    success: "Success",
    warning: "Warning",
    info: "Info",
    yes: "Yes",
    no: "No",
    all: "All",
    none: "None",
    select: "Select",
    for: "for",
    in: "in",
    showMore: "Show More",
    next: "Next",
    previous: "Previous",
    showLess: "Show Less",
    clearFilter: "Clear filter",
    searchCountry: "Search countries...",
    page: "Page",
    merchant: "Merchant",
    description: "Description",
    account: "Account",
    type: "Type",
    date: "Date",
    amount: "Amount",
    category: "Category",
    transactions: "Transactions",
    total: "Total",
    actions: "Actions",
    viewReport: "View Report",
    other: "Other",
    selectCountries: "Select countries",
    noCountryFound: "No country found.",
    continue: "Continue",
    or: "or",
  },

  // Time related
  time: {
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This Week",
    lastWeek: "Last Week",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    thisYear: "This Year",
    lastYear: "Last Year",
    custom: "Custom",
    from: "From",
    to: "To",
    now: "Now",
    quarter: "Quarter",
    year: "Year",
    month: "Month",
    period: "Period",
    startMonth: "Start Month",
    endMonth: "End Month",
    startQuarter: "Start Quarter",
    endQuarter: "End Quarter",
    lastQuarter: "Last 90 days",
    allTime: "All time",
  },

  // Navigation and menus
  nav: {
    passiveIncome: "Passive Income",
    dashboard: "Dashboard",
    transactions: "Transactions",
    accounts: "Accounts",
    categories: "Categories",
    reports: "Reports",
    settings: "Settings",
    users: "Users",
    logout: "Logout",
    login: "Login",
    register: "Register",
    profile: "Profile",
    preferences: "Preferences",
    help: "Help",
    support: "Support",
    about: "About",
    currencyRates: "Currency Rates",
    financialSummary: "Financial Summary",
    portfolioHoldings: "Portfolio Holdings",
    tagAnalytics: "Tag Analytics",
    equityAnalysis: "Equity Analysis",
    expenses: "Expenses",
    agents: "Bliss Agents",
    importAgent: "Import Agent",
    insightAgent: "AI Insights",
    travelAgent: "Travel Agent",
    manageAssets: "Manage Assets",
    assetsLiabilities: "Assets & Liabilities",
    transactionReview: "Transaction Review",
    subscriptions: "Subscriptions",
  },

  // Page titles and descriptions
  pages: {
    oauth: {
      consent: {
        title: "Connect {{client}} to Bliss",
        subtitle: "This app wants to use your Bliss data through the MCP server.",
        signed_in_as: "Signed in as {{email}}",
        redirect_notice: "After you decide, you'll be sent back to",
        access_label: "Access",
        read_only_description: "See transactions, portfolio, analytics and insights. It can't change anything.",
        read_write_description: "Also re-categorise transactions, work your review queues, and edit portfolio data.",
        expiry_label: "Connection expires after",
        no_expiry_warning: "A connection without an expiry keeps working until you revoke it.",
        footnote: "The app sees your decrypted financial data. You can revoke the connection at any time in Settings → Integrations.",
        admin_required: "Only a workspace admin can connect apps. Ask an admin to connect it for you.",
        allow: "Allow",
        deny: "Deny",
        error_title: "Can't complete this connection",
        error_missing: "This link is incomplete. Start the connection again from your app.",
        error_generic: "Something went wrong. Start the connection again from your app.",
      },
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Overview of your financial status",
      summary: "Summary",
      recentTransactions: "Recent Transactions",
      quickActions: "Quick Actions",
      insights: "Insights",
      totalNetWorth: "Total Net Worth",
    },
    accounts: {
      title: "Accounts",
      subtitle: "Manage your financial accounts",
      addAccount: "Add Account",
      editAccount: "Edit Account",
      deleteAccount: "Delete Account",
      accountDetails: "Account Details",
      noAccounts: "No accounts found",
      hasBeenRemoved: "has been removed",
    },
    transactions: {
      title: "Transactions",
      subtitle: "View and manage your transactions",
      addTransaction: "Add Transaction",
      editTransaction: "Edit Transaction",
      deleteTransaction: "Delete Transaction",
      transactionDetails: "Transaction Details",
      noTransactions: "No transactions found",
      noTransactionsFound: "No transactions found",
      tryDifferentFilters: "Try adjusting your filters to find what you're looking for.",
      filter: "Filter Transactions",
      import: "Import Transactions",
      export: "Export Transactions",
      allAccounts: "All Accounts",
      allGroups: "All Groups",
      allCategories: "All Categories",
      categoryGroup: "Category Group",
      exportCsv: "Export CSV",
      importCsv: "Import CSV",
      exportComplete: "Export complete",
      csvDownloaded: "CSV downloaded successfully",
      exportFailed: "Export failed",
      exporting: "Exporting...",
      exportTransactions: "Export Transactions",
      exportDescription: "Choose which transactions to export",
      currentFilters: "{{count}} transactions matching current filters",
      allTransactions: "All transactions",
      exportingTransactions: "Exporting your transactions...",
      exportingNote: "This may take a moment. Please don't close this dialog.",
      startDate: "Start Date",
      endDate: "End Date",
      pickDate: "Pick date",
      editTransactionDescription: "Edit the details of this transaction.",
      addTransactionDescription: "Fill in the details to add a new transaction.",
    },
    categories: {
      title: "Categories",
      subtitle: "Manage your transaction categories",
      addCategory: "Add Category",
      editCategory: "Edit Category",
      renameCategory: "Rename Category",
      deleteCategory: "Delete Category",
      categoryDetails: "Category Details",
      noCategories: "No categories found",
      hasBeenRemoved: "has been removed",
      renameDescription: "Default categories cannot be moved to a different type or group. You can update the name and icon.",
      addNewDescription: "Fill in the details to create a new custom category.",
      updateDetails: "Update the category details below.",
      customCategories: "Custom Categories",
      customEmpty: "No custom categories yet. Add one when the defaults don't fit your needs.",
      addToType: "+ Add {{type}} Category",
      filterByGroup: "Filter by Group",
      empty: {
        title: "No categories found",
        description: "Create your first category to get started.",
      },
      noMatch: {
        title: "No results found",
        description: "Try adjusting your search query.",
      },
    },
    reports: {
      title: "Reports",
      subtitle: "Select a report type to generate detailed financial insights",
      financialSummary: {
        title: "Financial Summary",
        description: "View income and spending over different time periods"
      },
      expenses: {
        title: "Expense Tracking",
        description: "Analyze spending patterns across categories"
      },
      portfolio: {
        title: "Portfolio Holdings",
        description: "View and analyze your investment portfolio"
      },
      recentReports: {
        title: "Recent Reports",
        description: "Your recently generated reports",
        empty: "No recent reports found.",
        hint: "Reports you generate will appear here for quick access."
      }
    },
    currencyRates: {
      title: "Currency Rates",
      subtitle: "Monitor and manage exchange rates",
      baseCurrency: "Base Currency",
      targetCurrency: "Target Currency",
      currentRate: "Current Rate",
      historicalRates: "Historical Rates",
      updateRate: "Update Rate",
    },
    financialSummary: {
      title: "Financial Summary",
      subtitle: "View and analyze your income and spending",
      statement: "Statement",
      trend: "Monthly Trend",
      breakdown: "Detailed breakdown of income and expenses",
      chartDescription: "Monthly breakdown of income, expenses, and savings",
      downloadStatement: "Download Full Statement",
      netIncome: "Net Income",
      netSavings: "Net Savings",
      totalRevenue: "Total Revenue",
      totalExpenses: "Total Expenses",
      savingsRate: "Savings Rate",
      topExpense: "Top Expense",
      discretionaryIncome: "Discretionary Income",
      savingsCapacity: "Savings Capacity",
      otherActivity: "Other Activity",
    },
    portfolio: {
      title: "Portfolio Holdings",
      description: "Track and analyze your investment portfolio by asset type",
      subtitle: "Track and analyze your investment portfolio by asset type",
      totalValue: "Total Portfolio Value",
      totalHoldings: "Across {{count}} holdings",
      gainLoss: "Total Gain/Loss",
      overallReturn: "{{value}}% overall return",
      assetBreakdown: "Asset Class Breakdown",
      holdings: "Holdings List",
      allocation: "Asset Allocation",
      performance: "Performance",
      holdingsDescription: "Your investments grouped by asset type",
      allocationDescription: "Distribution of your portfolio by asset type",
      performanceDescription: "Comparison of gains and losses across different asset types",
      search: "Search holdings...",
      assetAndType: "Asset & Type",
      symbolId: "Symbol/ID",
      qtyUnits: "Qty/Units",
      currentPrice: "Current Price",
      marketValue: "Market Value",
      gainLossValue: "Gain/Loss",
      percentReturn: "% Return"
    },
    expenses: {
      title: "Expense Tracking",
      description: "Analyze your spending patterns by category",
      selectCategoryType: "Select category type",
      countriesSelected: "{{count}} countries selected",
      includeCredits: {
        label: "Include credits",
        tooltip: "On: credits to expense categories (refunds, reimbursements, a flatmate's share, insurance payouts) are subtracted from debits. Off: shows debits only."
      },
      basis: {
        net: "debits − credits",
        debitOnly: "debits only"
      },
      timeFrame: {
        label: "Time Frame",
        month: "Month",
        quarter: "Quarter",
        year: "Year"
      },
      export: "Export",
      metrics: {
        totalExpenses: "Total Expenses",
        averageMonthly: "Average Monthly",
        highestCategory: "Highest Category",
        monthlyAverage: "Monthly Average"
      },
      breakdown: {
        title: "Expense Breakdown",
        description: "View your expenses by category",
        tabs: {
          byCategory: "By Category",
          monthlyTrends: "Monthly Trends",
          detailedList: "Detailed List"
        },
        topCategories: "Top Expense Categories",
        netCredits: "Net credits",
        netCreditsCaption: "Total includes {{amount}} of net credits",
        noNetSpend: "No net spend in this period",
        trendChart: {
          movingAverageSuffix: " (6-mo avg)"
        },
        comingSoon: {
          trends: "Monthly trend view will be available soon.",
          details: "Detailed expense list view will be available soon."
        },
        details: {
          emptyTitle: "Select a category to explore",
          emptyDescription: "Click on a slice in the pie chart or a category row to see individual transactions.",
          goToCategories: "Go to By Category",
          capNotice: "Showing the first 1,000 transactions; totals may be incomplete",
        }
      }
    },
    settings: {
      title: "Account Settings",
      description: "Manage your account preferences and settings",
      language: "Language",
      error_loading_reference_data: "Failed to load reference data. Please refresh the page.",
      error_saving_no_id: "Could not find your user ID to save settings.",
      error_saving_generic: "An unknown error occurred while saving.",
      success_saving: "Your settings have been saved successfully.",
      tabs: {
        general: "General",
        countries_currencies: "Countries & Currencies",
        banks: "Banks",
        connections: "Connections",
        integrations: "Integrations",
        ai_classification: "AI Classification",
        maintenance: "Maintenance",
        admin_section: "Administration",
        processing: "Processing",
        bank_sync: "Bank sync",
      },
      general: {
        title: "General Settings",
        description: "Configure your tenant general settings and information",
        tenant_name_label: "Tenant Name",
        tenant_name_description: "This is the name of your organization or personal finance profile.",
        user_management_label: "User Management",
        user_management_button: "Manage Users",
        user_management_description: "Manage users with access to your tenant account.",
        sign_out_others: {
          label: "Other devices",
          description: "Sign out everywhere except this device. Your other browsers and installed apps will need to sign in again.",
          button: "Sign out of all other devices",
          confirm_title: "Sign out of all other devices?",
          confirm_description: "Every other browser and installed app signed in to your account is signed out on its next request. This device stays signed in.",
          confirm: "Sign out others",
          success: "Other devices have been signed out.",
          error: "Could not sign out other devices. Please try again.",
        },
      },
      countries: {
        title: "Countries",
        description: "Countries where you have financial activities",
        selected_label: "Selected Countries",
        available_label: "Available Countries",
        none_selected: "No countries selected"
      },
      currencies: {
        title: "Currencies",
        description: "Currencies you use in your transactions",
        selected_label: "Selected Currencies",
        available_label: "Available Currencies",
        none_selected: "No currencies selected"
      },
      banks: {
        title: "Bank Settings",
        description: "Manage the banks you use in your financial activities",
        selected_label: "Your Banks",
        available_label: "Available Banks",
        none_selected: "No banks selected"
      },
      connections: {
        title: "Plaid Connections",
        description: "Manage your linked bank connections and their sync status",
        no_connections: "No bank connections yet. Use 'Connect Bank' to link your first account.",
        status_active: "Active",
        status_login_required: "Login Required",
        status_error: "Error",
        status_revoked: "Revoked",
        status_pending: "Pending",
        last_sync: "Last sync",
        never_synced: "Never synced",
        error_code: "Error",
        reconnect: "Reconnect",
        reconnect_description: "Re-authenticate your bank connection to resume syncing.",
        connection_warning: "One or more bank connections require attention.",
        accounts_linked: "accounts linked",
      },
      integrations: {
        title: "Integrations & API tokens",
        description: "Let AI agents, scripts and other systems use the Bliss API. Each integration gets its own tokens, which you can revoke at any time.",
        create: "New integration",
        create_title: "New integration",
        add_key_title: "Add a key",
        rename_title: "Rename integration",
        empty: "No integrations yet. Create one to connect an AI agent or another system.",
        loading_error: "Could not load integrations.",
        acting_note: "Token requests act on your behalf, limited to the access level you choose. If your user is deleted, your integrations are revoked.",
        security_note: "Anyone holding a token can see your decrypted financial data. Treat it like a bank password.",
        access_read_only: "Read-only",
        access_read_write: "Read & write",
        access_read_only_description: "Can read transactions, portfolio, analytics and insights. Cannot change anything.",
        access_read_write_description: "Can also create and edit transactions, tags and imports, and work through the Plaid review queue. Cannot manage accounts, categories, users, bank connections or settings.",
        status_active: "Active",
        status_revoked: "Revoked",
        status_expired: "Expired",
        keys_count: "{{count}} of {{total}} keys active",
        last_used: "Last used",
        never_used: "Never",
        expires: "Expires",
        no_expiry: "No expiry",
        add_key: "Add key",
        revoke_key: "Revoke",
        revoke_integration: "Revoke integration",
        rename: "Rename",
        show_keys: "Keys",
        hide_keys: "Hide keys",
        form: {
          name_label: "Name",
          name_placeholder: "e.g. Claude agent",
          name_required: "Enter a name.",
          description_label: "Description (optional)",
          access_label: "Access level",
          access_fixed: "The access level can't be changed later. To change it, create a new integration.",
          key_name_label: "Key name (optional)",
          key_name_placeholder: "e.g. Laptop",
          expiry_label: "Key expires after",
          expiry_30: "30 days",
          expiry_90: "90 days",
          expiry_365: "1 year",
          expiry_never: "Never",
          no_expiry_warning: "A key without an expiry keeps working until you revoke it.",
          create_submit: "Create integration",
          add_key_submit: "Create key",
          save: "Save",
          cancel: "Cancel",
        },
        reveal: {
          title: "Copy your token",
          subtitle: "Use it as a Bearer token in the Authorization header.",
          warning: "This token won't be shown again. Store it somewhere safe, such as a password manager.",
          copy: "Copy",
          copied: "Copied",
          example: "Example request",
          done: "Done",
        },
        mcp: {
          title: "Use with Claude (MCP)",
          description: "Connect Claude Code or another MCP client with this key. It sees the same data the key allows: read-only keys can only read.",
          url_label: "MCP server URL:",
          copy_command: "Copy command",
        },
        oauth: {
          badge: "Connected via OAuth · {{client}}",
          expires: "Connection expires {{date}}",
          no_expiry: "Connection never expires",
        },
        confirm: {
          revoke_key_title: "Revoke this key?",
          revoke_key_description: "Anything using this key stops working on its next request. The integration's other keys keep working.",
          revoke_integration_title: "Revoke this integration?",
          revoke_integration_description: "All of its keys stop working immediately. This can't be undone.",
          confirm_revoke: "Revoke",
        },
        toast: {
          created: "Integration created",
          key_created: "Key created",
          key_revoked: "Key revoked",
          revoked: "Integration revoked",
          renamed: "Integration updated",
          error: "Something went wrong. Please try again.",
        },
      },
    },
    users: {
      title: "Users",
      subtitle: "Manage user accounts and permissions",
      addUser: "Add User",
      editUser: "Edit User",
      deleteUser: "Delete User",
      userDetails: "User Details",
      noUsers: "No users found",
      roles: "Roles and Permissions",
    },
    notFound: {
      title: "Page Not Found",
      subtitle: "The page you are looking for does not exist",
      goBack: "Go back to homepage",
    },
    login: {
      confirm_identity: {
        title: "Confirm it's you",
        description: "For your security, please confirm your identity to continue. This is needed when your last sign-in was more than 15 minutes ago.",
        password_label: "Password",
        confirm: "Confirm",
        google: "Continue with Google",
        divider: "or",
        wrong_password: "Incorrect password. Please try again.",
        error: "Could not confirm your identity. Please try again.",
        no_method: "This account cannot confirm its identity here. Sign out and sign in again, then retry.",
      },
      title: "Login",
      subtitle: "Sign in to your account",
      noAccount: "Don't have an account?",
      signUp: "Sign up",
      rememberMe: "Remember me",
    },
    agents: {
      title: "Bliss Agents",
      subtitle: "AI-powered financial assistants",
    },
    importAgent: {
      title: "Import Agent",
      subtitle: "Import and process financial data from various sources",
      description: "Upload and automatically categorize transactions from CSV/PDF statements",
      dropzone: "Drop files here or click to browse",
      processing: "Processing your documents",
      success: "Successfully imported",
      failed: "Failed to import",
      supportedFormats: "Supported formats: CSV, PDF, Excel",
      parseOptions: "Parsing options",
      mapColumns: "Map columns",
      preview: "Preview data",
      import: "Import data",
    },
    insightAgent: {
      title: "AI Insights",
      subtitle: "Get personalized financial insights and recommendations",
      description: "Analyze your spending patterns and receive tailored financial advice",
      generating: "Generating insights",
      noData: "Need more transaction data to generate insights",
      categories: "Category insights",
      trends: "Spending trends",
      recommendations: "Recommendations",
      savingOpportunities: "Saving opportunities",
      budgetSuggestions: "Budget suggestions",
    },
    travelAgent: {
      title: "Travel Agent",
      subtitle: "Manage your travel expenses and budgets",
      description: "Plan trips, track expenses, and optimize travel spending",
      upcoming: "Upcoming trips",
      past: "Past trips",
      expenses: "Travel expenses",
      currency: "Currency conversion",
      planning: "Trip planning",
      budgeting: "Trip budgeting",
      addTrip: "Add trip",
      editTrip: "Edit trip",
    },
    comingSoon: {
      title: "Coming Soon",
      subtitle: "This feature is under development",
      description: "We are working hard to bring you this feature. Check back soon!",
      backToHome: "Back to Dashboard",
    },
    manualUpdates: {
      title: "Assets & Liabilities",
      subtitle: "Manage your asset prices and debt terms",
      assetsNeedingUpdate: "Assets Needing Price Update",
      liabilities: "Liabilities",
      noAssetsNeedUpdate: "All assets are up to date.",
      noLiabilities: "No liabilities found.",
      lastUpdated: "Last updated",
      daysAgo: "{{count}} days ago",
      updatePrice: "Update Price",
      editTerms: "Edit Terms",
      errorLoading: "Failed to load asset data. Please try again later.",
    },
    profile: {
      title: "Profile",
      subtitle: "Manage your personal information",
    },
  },
  
  // Form fields and validation
  forms: {
    required: "This field is required",
    invalidEmail: "Please enter a valid email address",
    passwordLength: "Password must be at least 8 characters long",
    passwordMismatch: "Passwords do not match",
    invalidAmount: "Please enter a valid amount",
    invalidDate: "Please enter a valid date",
    maxLength: "Maximum length exceeded",
    minLength: "Minimum length required",
    selectOption: "Please select an option",
    invalidInput: "Invalid input",
  },

  // Dashboard cards and widgets
  dashboard: {
    totalBalance: "Total Balance",
    income: "Income",
    expenses: "Expenses",
    savings: "Savings",
    budget: "Budget",
    recentTransactions: "Recent Transactions",
    cashFlow: "Cash Flow",
    netWorth: "Net Worth",
    spendingByCategory: "Living Expenses by Category",
    topCategories: "Top Categories",
    monthlyComparison: "Monthly Comparison",
    viewAll: "View All",
    viewMore: "View More",
    seeAll: "See All",
    welcome: "Welcome back, {{name}}",
    quickActions: "Quick Actions",
    commonTasks: "Common tasks",
    syncedAccounts: "Synced Accounts",
    connected: "connected",
    allCurrent: "all current",
    noAccountsConnected: "No accounts connected yet",
    connectMoreAccounts: "Connect more accounts",
    allConnectedAccounts: "All connected accounts",
    noTransactionsYet: "No transactions yet",
    viewAllTransactions: "View all transactions",
    live: "Live",
    actionNeeded: "Action needed",
  },

  // Charts and graphs
  charts: {
    period: "Period",
    value: "Value",
    change: "Change",
    percentage: "Percentage",
    currency: "Currency",
    category: "Category",
    amount: "Amount",
    date: "Date",
    trend: "Trend",
    average: "Average",
    total: "Total",
    count: "Count",
    min: "Minimum",
    max: "Maximum",
    distribution: "Distribution",
    comparison: "Comparison",
  },

  // Asset Types
  assetTypes: {
    Equity: {
      name: "Equity",
      description: "Stocks, ETFs, and other equity securities"
    },
    Crypto: {
      name: "Crypto",
      description: "Bitcoin, Ethereum, and other cryptocurrencies"
    },
    RealEstate: {
      name: "Real Estate",
      description: "Residential and commercial properties"
    },
    Bonds: {
      name: "Bonds",
      description: "Government and corporate bonds"
    },
    PensionPlan: {
      name: "Pension Plan",
      description: "Retirement and pension plans"
    },
    Stocks: {
      name: "Stocks",
      description: "Individual company shares"
    },
    Funds: {
      name: "Funds",
      description: "Mutual funds and investment funds"
    },
    ETFs: {
      name: "ETFs",
      description: "Exchange-traded funds"
    },
    Cash: {
      name: "Cash",
      description: "Cash and cash equivalents"
    },
    Collectible: {
      name: "Collectible",
      description: "Art, collectibles, and other unique items"
    },
    Vehicle: {
      name: "Vehicle",
      description: "Cars, boats, and other vehicles"
    },
    Other: {
      name: "Other",
      description: "Other miscellaneous assets"
    }
  },

  // Transaction types and categories
  transactions: {
    income: "Income",
    expense: "Expense",
    transfer: "Transfer",
    investment: "Investment",
    withdrawal: "Withdrawal",
    deposit: "Deposit",
    payment: "Payment",
    refund: "Refund",
    fee: "Fee",
    tax: "Tax",
    dividend: "Dividend",
    interest: "Interest",
    sale: "Sale",
    purchase: "Purchase",
    other: "Other",
    // Transaction categories
    housing: "Housing",
    food: "Food",
    transportation: "Transportation",
    utilities: "Utilities",
    healthcare: "Healthcare",
    personal: "Personal",
    entertainment: "Entertainment",
    education: "Education",
    investments: "Investments",
    ventures: "Ventures",
  },

  // Financial Summary specific
  financialSummary: {
    revenue: "Revenue",
    assetTransfer: "Asset Transfer",
    housingUtilities: "Housing & Utilities",
    livingExpenses: "Living Expenses",
    personalDevelopment: "Personal Development",
    investments: "Investments",
    housing: "Housing",
    maintenance: "Maintenance",
    utilities: "Utilities",
    eatingOut: "Eating Out",
    eatingIn: "Eating In",
    healthBeauty: "Health & Beauty",
    shopping: "Shopping",
    entertainment: "Entertainment",
    travel: "Travel",
    education: "Education",
    skills: "Skills",
    international: "International",
    domestic: "Domestic",
    laborIncome: "Labor Income",
    taxes: "Taxes",
    ventures: "Ventures",
    angelInvestments: "Angel Investments",
    variableIncome: "Variable Income",
    gambling: "Gambling",
    margin: "margin",
    netIncome: 'Net Income',
    discretionaryIncome: 'Discretionary Income',
    savingsCapacity: 'Savings Capacity',
    netSavings: 'Net Savings',
    otherActivity: 'Other Activity',
    netInvested: "Net invested",
    netInvestedTooltip: "Money moved into investments minus money taken out (sales, withdrawals). Positive means you invested more than you took out. This is not a gain or a loss.",
    netDebtRepaid: "Net debt repaid",
    netDebtRepaidTooltip: "Debt paid down minus new borrowing (for example, a loan deposited by your bank). Positive means you reduced your debt.",
    quarters: {
      q1: "Q1",
      q2: "Q2",
      q3: "Q3",
      q4: "Q4",
    },
  },

  // UI elements
  ui: {
    darkMode: "Dark Mode",
    lightMode: "Light Mode",
    systemMode: "System Mode",
    close: "Close",
    open: "Open",
    collapse: "Collapse",
    expand: "Expand",
    refresh: "Refresh",
    retry: "Retry",
    rename: "Rename",
    edit: "Edit",
    delete: "Delete",
    deleting: "Deleting...",
    cancel: "Cancel",
    saveChanges: "Save Changes",
    clearFilters: "Clear Filters",
    dialog: {
      deleteTitle: "Delete {{entity}}",
      deleteConfirmation: "Are you sure you want to delete \"{{name}}\"? This action cannot be undone.",
    },
    entity: {
      category: "Category",
      account: "Account",
    },
    loading: "Loading...",
    error: "Error",
    success: "Success",
    warning: "Warning",
    info: "Info",
    notification: "Notification",
    alert: "Alert",
    dialogElement: "Dialog",
    modal: "Modal",
    drawer: "Drawer",
    toast: "Toast",
    tooltipElement: "Tooltip",
    badge: "Badge",
    avatar: "Avatar",
    icon: "Icon",
    button: "Button",
    input: "Input",
    select: "Select",
    checkbox: "Checkbox",
    radio: "Radio",
    toggle: "Toggle",
    slider: "Slider",
    range: "Range",
    datepicker: "Date Picker",
    timepicker: "Time Picker",
    colorpicker: "Color Picker",
    filepicker: "File Picker",
    pagination: "Pagination",
    table: "Table",
    list: "List",
    card: "Card",
    tabs: "Tabs",
    accordion: "Accordion",
    menu: "Menu",
    dropdown: "Dropdown",
    popover: "Popover",
    breadcrumb: "Breadcrumb",
    navigation: "Navigation",
    sidebar: "Sidebar",
    header: "Header",
    footer: "Footer",
    content: "Content",
    layout: "Layout",
    gridLayout: "Grid",
    column: "Column",
    row: "Row",
    section: "Section",
    container: "Container",
    wrapper: "Wrapper",
    box: "Box",
    flexbox: "Flexbox",
    stack: "Stack",
    group: "Group",
    divider: "Divider",
    spacer: "Spacer",
    separator: "Separator",
    spinner: "Spinner",
    progress: "Progress",
    skeleton: "Skeleton",
    placeholder: "Placeholder",
    empty: "Empty",
    fallback: "Fallback",
    errorState: "Error",
    successState: "Success",
    warningState: "Warning",
    infoState: "Info",
    across: "Across",
    upgradeToPro: "Upgrade to Pro",
    upgradeDescription: "Get advanced insights and features",
    upgradeNow: "Upgrade now",
  },

  // Notifications and alerts
  notifications: {
    success: {
      saved: "Successfully saved!",
      created: "Successfully created!",
      updated: "Successfully updated!",
      deleted: "Successfully deleted!",
      imported: "Successfully imported!",
      exported: "Successfully exported!",
      uploaded: "Successfully uploaded!",
      downloaded: "Successfully downloaded!",
      sent: "Successfully sent!",
      submitted: "Successfully submitted!",
    },
    error: {
      title: "Error",
      general: "An error occurred. Please try again.",
      generic: "An error occurred. Please try again.",
      notFound: "Resource not found.",
      unauthorized: "Unauthorized access.",
      forbidden: "Access forbidden.",
      validation: "Validation error. Please check your input.",
      server: "Server error. Please try again later.",
      network: "Network error. Please check your connection.",
      timeout: "Request timed out. Please try again.",
      conflict: "Conflict error. Resource already exists.",
      badRequest: "Bad request. Please check your input.",
      unavailable: "Service unavailable. Please try again later.",
      tooManyRequests: "Too many requests. Please try again later.",
      deleteCategory: "Failed to delete category. Please try again.",
    },
    warning: {
      unsavedChanges: "You have unsaved changes. Are you sure you want to leave?",
      deleteConfirmation: "Are you sure you want to delete this item?",
      limitReached: "You have reached the limit.",
      lowBalance: "Your balance is low.",
      expiringSoon: "Expiring soon.",
    },
    info: {
      noResults: "No results found.",
      empty: "No items to display.",
      loading: "Loading...",
      processingRequest: "Processing your request...",
      newVersion: "A new version is available.",
      comingSoon: "Coming soon!",
      maintenance: "Scheduled maintenance.",
    },
    center: {
      title: "Notifications",
      allCaughtUp: "All caught up",
    },
  },

  accountForm: {
    accountName: "Account Name",
    accountNumber: "Account Number",
    bank: "Bank / Institution",
    owners: "Owners",
    currency: "Currency",
    country: "Country",
    accountCreated: "Account created",
    accountCreatedSuccess: "{{name}} has been added to your accounts.",
    accountUpdated: "Account updated",
    accountUpdatedSuccess: "The account has been updated successfully.",
    createAccount: "Create Account",
    updateAccount: "Update Account",
    createError: "Failed to create account. Please try again.",
    updateError: "Failed to update account. Please try again.",
    selectBank: "Select a bank",
    selectCurrency: "Select a currency",
    selectCountry: "Select a country",
    addBank: "Add a new bank",
    addBankError: "Failed to add bank. Please try again.",
  },
  addBankDialog: {
    title: "Add a new bank",
    description: "Enter the name of the bank you'd like to add.",
    nameLabel: "Bank name",
    namePlaceholder: "e.g. Chase, Monzo, Revolut",
    validationError: "Bank name must be between 2 and 100 characters.",
  },
  categoryForm: {
    name: "Category Name",
    type: "Type",
    group: "Group",
    categoryCreated: "Category created",
    categoryCreatedSuccess: "{{name}} has been added to your categories.",
    categoryUpdated: "Category updated",
    categoryUpdatedSuccess: "The category has been updated successfully.",
    createCategory: "Create Category",
    updateCategory: "Update Category",
    createError: "Failed to create category. Please try again.",
    updateError: "Failed to update category. Please try again.",
  },
  transactionForm: {
    date: "Date",
    transactionType: "Transaction Type",
    description: "Description",
    amount: "Amount",
    fromAccount: "From Account",
    account: "Account",
    toAccount: "To Account",
    category: "Category",
    notes: "Notes (Optional)",
    transactionCreated: "Transaction created",
    transactionCreatedSuccess: "New transaction has been recorded.",
    transactionUpdated: "Transaction updated",
    transactionUpdatedSuccess: "The transaction has been updated successfully.",
    createTransaction: "Create Transaction",
    updateTransaction: "Update Transaction",
    createError: "Failed to create transaction. Please try again.",
    updateError: "Failed to update transaction. Please try again.",
  },
  userForm: {
    email: "Email",
    userCreated: "User created",
    userCreatedSuccess: "{{email}} has been invited to your team.",
    userUpdated: "User updated",
    userUpdatedSuccess: "The user has been updated successfully.",
    inviteUser: "Invite User",
    updateUser: "Update User",
    createError: "Failed to create user. Please try again.",
    updateError: "Failed to update user. Please try again.",
  },

  // Default category translations (keyed by defaultCategoryCode)
  defaultCategories: {
    names: {
      OPERATING_CASH: "Operating Cash",
      STOCKS: "Stocks",
      INVESTMENT_FUNDS: "Funds",
      ETFS: "ETFs",
      COPY_TRADING: "Copy Trading",
      PENSION_PLAN: "Pension Plan",
      GOVERNMENT_BONDS: "Government Bonds",
      CORPORATE_BONDS: "Corporate Bonds",
      CRYPTO: "Crypto",
      REAL_ESTATE: "Real Estate",
      COLLECTIBLE: "Collectible",
      VEHICLE: "Vehicle",
      COMMODITIES: "Commodities",
      PRIVATE_EQUITY: "Private Equity",
      MORTGAGE: "Mortgage",
      CREDIT_CARD_DEBT: "Credit Card Debt",
      ALLOWANCE: "Allowance",
      RENT_INCOME: "Rent Income",
      SALARY: "Salary",
      FREELANCE_INCOME: "Freelance Income",
      BONUS: "Bonus",
      COMMISSION: "Commission",
      GOVERNMENT_WELFARE: "Government Welfare",
      TAXES: "Taxes",
      TAX_REFUND: "Tax Refund",
      DIVIDENDS: "Dividends",
      BOND_INCOME: "Bond Income",
      INTEREST_INCOME: "Interest Income",
      OPTIONS_INCOME: "Options Income",
      RENT: "Rent",
      HOA_COMMUNITY_FEES: "HOA / Community fees",
      SECURITY_DEPOSIT: "Security Deposit",
      HOME_INSURANCE: "Home Insurance",
      POWER_AND_GAS: "Power & Gas",
      WATER: "Water",
      DATA_PLAN: "Data Plan",
      INTERNET: "Internet",
      CLEANING_SERVICE: "Cleaning service",
      HOME_IMPROVEMENT: "Home Improvement",
      GROCERIES: "Groceries",
      CONVENIENCE_STORES: "Convenience stores",
      DOCTOR: "Doctor",
      HEALTH_INSURANCE: "Health Insurance",
      PHARMACY: "Pharmacy",
      BIKING: "Biking",
      FUEL_AND_GAS: "Fuel & Gas",
      VEHICLE_MAINTENANCE: "Vehicle Maintenance",
      VEHICLE_TAXES_AND_TICKETS: "Vehicle Taxes & Tickets",
      VEHICLE_INSURANCE: "Vehicle Insurance",
      METRO: "Metro",
      PARKING: "Parking",
      TAXI: "Taxi",
      TRAINS: "Trains",
      BUS: "Bus",
      PET_CARE: "Pet care",
      PET_FOOD: "Pet food",
      PET_INSURANCE: "Pet insurance",
      BANKING_FEES: "Banking fees",
      DOCUMENTATION: "Documentation",
      NOTARY: "Notary",
      ACCOUNTING: "Accounting",
      BUSINESS_MEAL: "Business Meal",
      RESTAURANTS: "Restaurants",
      BAKERY_AND_COFFEE: "Bakery & Coffee",
      FOOD_DELIVERY: "Food Delivery",
      BAR: "Bar",
      NIGHTCLUB: "Nightclub",
      CONCERTS: "Concerts",
      MOVIES_AND_THEATER: "Movies & Theater",
      OUTDOOR_ACTIVITIES: "Outdoor Activities",
      SPORTING_EVENTS: "Sporting Events",
      GAMBLING: "Gambling",
      GAMING: "Gaming",
      BEAUTY: "Beauty",
      HAIRCARE: "Haircare",
      SPORTS_AND_GYM: "Sports & Gym",
      SHOPPING: "Shopping",
      ELECTRONICS: "Electronics",
      FURNITURE_AND_DIY: "Furniture & DIY",
      CLOTHES_AND_FASHION: "Clothes & Fashion",
      GIFTS: "Gifts",
      JEWELRY: "Jewelry",
      DECORATION_AND_TEXTILES: "Decoration & textiles",
      KITCHENWARE: "Kitchenware",
      SOFTWARE: "Software",
      CONTENT_AND_MEDIA: "Content & Media",
      LOYALTY_PROGRAMS: "Loyalty Programs",
      CASH_AT_ATM: "Cash at ATM",
      INHERITANCE: "Inheritance",
      RECEIVED_DOMESTIC: "Transfer Received — Domestic",
      RECEIVED_INTERNATIONAL: "Transfer Received — International",
      SENT_DOMESTIC: "Transfer Sent — Domestic",
      SENT_INTERNATIONAL: "Transfer Sent — International",
      BIZ_SALES_REVENUE: "Sales Revenue",
      BIZ_SERVICE_REVENUE: "Service Revenue",
      BIZ_REFERRAL_INCOME: "Referral & Affiliate",
      BIZ_INVENTORY: "Inventory & Materials",
      BIZ_SHIPPING: "Shipping & Logistics",
      BIZ_SAAS: "SaaS & Tools",
      BIZ_CLOUD: "Cloud & Hosting",
      BIZ_MARKETING: "Marketing & Advertising",
      BIZ_DATA_SERVICES: "Data & API Services",
      BIZ_OFFICE: "Office & Workspace",
      BIZ_DOMAINS: "Domains & Licensing",
      BIZ_EQUIPMENT: "Business Equipment",
      BIZ_OTHER_COSTS: "Other Business Costs",
      BIZ_FREELANCERS: "Freelancer & Contractor",
      BIZ_PROFESSIONAL_SERVICES: "Professional Services",
      BIZ_TAXES: "Business Taxes & VAT",
      BIZ_CAPITAL: "Capital Injection",
      MUSIC: "Musical Instruments",
      THERAPY_AND_COUNSELING: "Therapy & Counseling",
      MENTORING: "Coaching & Mentoring",
      SCHOOL: "School",
      ONLINE_COURSES: "Online Course",
      TRAVEL_TRANSPORT: "Travel Transport",
      ACCOMMODATION: "Accommodation",
      CAR_RENTAL: "Car Rental",
      TOURS_AND_ACTIVITIES: "Tours & Activities",
      TRAVEL_EXPENSES: "Travel Expenses",
      DONATIONS: "Donations",
      BOOKS: "Books",
    },
    descriptions: {
      SENT_DOMESTIC: "Money you sent to someone within your country — pay-back to friends, transfers between your own accounts, or any non-purchase debit.",
      SENT_INTERNATIONAL: "Money you sent across borders — wires to family abroad, transfers to your own foreign accounts, or any cross-border non-purchase debit.",
      RECEIVED_DOMESTIC: "Money received from someone within your country — pay-back from friends, transfers between your own accounts, or other non-income credits.",
      RECEIVED_INTERNATIONAL: "Money received from across borders — gifts from family abroad, transfers from your own foreign accounts, or other cross-border non-income credits.",
      BIZ_SERVICE_REVENUE: "Revenue from services rendered by your own business or side project. If freelancing is your main job rather than a side venture, use Freelance Income (under Labor Income) instead.",
      BIZ_SALES_REVENUE: "Revenue from goods sold by your own business or side project.",
      BIZ_OTHER_REVENUE: "Other revenue sources from your own business or side project — licensing fees, royalties, partner payouts, etc.",
      ALLOWANCE: "Pocket money, parental allowances, or ongoing family support. Useful for students and Bliss-in-classroom deployments where users track money received from family.",
      GOVERNMENT_WELFARE: "Government transfer payments — unemployment benefits, child benefits, social security, or similar. Not labor income.",
      INHERITANCE: "Money or assets inherited from a deceased family member. Filed under Transfers because it's a one-time windfall, not labor or investment income — keeping it here avoids distorting yearly income trends.",
      FREELANCE_INCOME: "Income from freelance or consulting work treated as your main job. If the work is a side business with its own P&L, use Service Revenue (under Ventures) instead.",
      BONUS: "Annual, quarterly, or one-off performance bonuses paid by your employer. Often taxed differently from regular salary — separating it makes income trends easier to read.",
      COMMISSION: "Sales commissions or other performance-based pay tied to revenue you generated.",
      TAX_REFUND: "Refund received from tax authorities for over-payment in a prior period.",
    },
    groups: {
      Cash: "Cash",
      Stocks: "Stocks",
      Funds: "Funds",
      ETFs: "ETFs",
      "Pension Plan": "Pension Plan",
      Bonds: "Bonds",
      Crypto: "Crypto",
      "Real Estate": "Real Estate",
      Collectible: "Collectible",
      "Depreciating assets": "Depreciating assets",
      Commodities: "Commodities",
      "Private Equity": "Private Equity",
      "Real Estate Loan": "Real Estate Loan",
      "Personal Debt": "Personal Debt",
      "Passive Income": "Passive Income",
      "Labor Income": "Labor Income",
      Taxes: "Taxes",
      Housing: "Housing",
      Utilities: "Utilities",
      "Home Maintenance": "Home Maintenance",
      "Eating In": "Eating In",
      Healthcare: "Healthcare",
      Transportation: "Transportation",
      Pets: "Pets",
      Finance: "Finance",
      Civil: "Civil",
      "Dining Out": "Dining Out",
      Entertainment: "Entertainment",
      "Wellness & Beauty": "Wellness & Beauty",
      "Shopping & Gifts": "Shopping & Gifts",
      Subscriptions: "Subscriptions",
      Misc: "Misc",
      Inheritance: "Inheritance",
      Domestic: "Domestic",
      International: "International",
      Revenue: "Revenue",
      "Cost of Goods Sold": "Cost of Goods Sold",
      "Operating Expenses": "Operating Expenses",
      "People & Services": "People & Services",
      Capital: "Capital",
      Skills: "Skills",
      "Mental Health": "Mental Health",
      Education: "Education",
      Travel: "Travel",
      Donations: "Donations",
      Other: "Other",
      Others: "Others",
      "N/A": "N/A",
    },
    types: {
      Income: "Income",
      Essentials: "Essentials",
      Lifestyle: "Lifestyle",
      Growth: "Growth",
      Ventures: "Ventures",
      Investments: "Investments",
      Asset: "Asset",
      Debt: "Debt",
      Transfers: "Transfers",
    },
    processingHints: {
      API_STOCK: "Stock Prices API",
      API_CRYPTO: "Crypto Prices API",
      API_FUND: "Fund Prices API",
      AMORTIZING_LOAN: "Loan Tracking",
      SIMPLE_LIABILITY: "Liability Tracking",
      CASH: "Cash Tracking",
      MANUAL: "Manual",
      TAX_DEDUCTIBLE: "Tax Deductible",
      DEBT: "Debt Tracking",
      COPY_TRADING: "eToro Copy Trading",
    },
  },

  // Accounts page
  accountsPage: {
    title: "Accounts",
    subtitle: "Manage your bank connections and manual accounts.",
    connectBank: "Connect Bank Account",
    addManual: "Add Manual Account",
    selectAccount: "Select an account",
    selectAccountHint: "Choose an account from the list to view connection details, sync logs, and management actions.",
    deleteTitle: "Delete Account",
    deleteConfirm: "Are you sure you want to delete \"{{name}}\"? This action cannot be undone.",
    deleteSuccess: "Account deleted",
    deleteSuccessDetail: "{{name}} has been removed.",
    deleteFailed: "Failed to delete account",
    deleteAction: "Delete Account",
    dangerZone: "Danger Zone",
    deleteBlockedTransactions_one: "This account has {{count}} linked transaction and cannot be deleted until it is removed or reassigned to another account.",
    deleteBlockedTransactions_other: "This account has {{count}} linked transactions and cannot be deleted until they are removed or reassigned to another account.",
    deleteBlockedTransactions: "This account has {{count}} linked transactions and cannot be deleted until they are removed or reassigned to another account.",
    deleteBlockedPlaid: "This account is still connected to your bank. Disconnect the bank connection first, then delete the account.",
    deleteBlockedPlaidHint: "Disconnect this bank connection first (use \"Pause Sync\" above) before deleting the account.",
    deleteUnknownError: "Couldn't delete this account. Please try again.",
    searchAccounts: "Search accounts...",
    noAccountsMatch: "No accounts match your search.",
    noAccountsYet: "No accounts yet.",
    synced: "Synced",
    actionRequired: "Action Required",
    disconnected: "Disconnected",
    manual: "Manual",
  },

  // Account detail panel
  accountDetail: {
    edit: "Edit",
    plaidConnected: "Plaid Connected",
    placeholderNumberHint: "This account still has a placeholder number from setup.",
    addAccountNumber: "Add the real account number",
    connectionHealth: "Connection Health",
    consentExpired: "Consent expired",
    consentExpiringSoon: "Consent expiring soon",
    consentExpiredOn: "Your bank consent expired on {{date}}. Reconnect to resume syncing.",
    consentExpiresOn: "Your bank consent expires on {{date}} ({{days}} day remaining). Reconnect before then to avoid interruption.",
    consentExpiresOnPlural: "Your bank consent expires on {{date}} ({{days}} days remaining). Reconnect before then to avoid interruption.",
    reconnect: "Reconnect",
    disconnectedMessage: "This bank connection was disconnected. Re-link to resume syncing transactions.",
    reconnectBank: "Reconnect Bank",
    actions: "Actions",
    resyncNow: "Resync Now",
    rotateToken: "Rotate Token",
    relinkPlaid: "Re-link Plaid",
    pauseSync: "Pause Sync",
    security: "Security",
    securityEncryption: "All account data is encrypted at rest using AES-256-GCM.",
    securityTokens: "Plaid access tokens are rotated periodically and stored encrypted.",
    pauseSyncDescription: "This will pause syncing for this account. Your existing transactions and connection history will not be affected. You can reconnect at any time to resume.",
    disconnect: "Disconnect",
    disconnecting: "Disconnecting...",
    syncTriggered: "Sync triggered",
    syncTriggeredDetail: "A new sync has been started for this account.",
    syncFailed: "Failed to trigger sync",
    tokenRotated: "Token rotated",
    tokenRotatedDetail: "Access token has been rotated successfully.",
    tokenRotateFailed: "Failed to rotate token",
    disconnected: "Disconnected",
    disconnectedDetail: "Bank connection has been removed.",
    disconnectFailed: "Failed to disconnect",
    securityDesc: "All account data is encrypted at rest using AES-256-GCM.",
    securityPlaid: "Plaid access tokens are rotated periodically and stored encrypted.",
    syncTriggeredDesc: "A new sync has been started for this account.",
    tokenRotatedDesc: "Access token has been rotated successfully.",
    disconnectedDesc: "Bank connection has been removed.",
    reconnectDesc: "This bank connection was disconnected. Re-link to resume syncing transactions.",
    pauseSyncTitle: "Pause Sync",
    pauseSyncDesc: "This will pause syncing for this account. Your existing transactions and connection history will not be affected. You can reconnect at any time to resume.",
    consentExpiredDesc: "Your bank consent expired on {{date}}. Reconnect to resume syncing.",
    consentExpiringSoonDesc: "Your bank consent expires on {{date}} ({{days}} days remaining). Reconnect before then to avoid interruption.",
  },

  // Add/edit account modal
  accountModal: {
    editTitle: "Edit Account",
    addTitle: "Add Manual Account",
    editDescription: "Update your account details.",
    addDescription: "Add a new manually-tracked account.",
  },

  // Connection health
  connectionHealth: {
    status: "Status",
    lastSynced: "Last Synced",
    nextSync: "Next Sync",
    approxInterval: "~6 hours",
    historyRange: "History Range",
    notYetSynced: "Not yet synced",
    historyStatus: "History Status",
    complete: "Complete",
    syncingHistory: "Syncing full history...",
    institutionId: "Institution ID",
    connection: "Connection",
    manualEntry: "Manual Entry",
    fetchOlder: "Fetch older transactions",
    selectStartDate: "Select a start date (up to 2 years back)",
    fetchFrom: "Fetch from",
    selectDate: "Select a date",
    title: "Connection Health",
    backfillStarted: "Backfill started",
    backfillDetail: "Fetching transactions from {{date}}. This may take a moment.",
    backfillFailed: "Failed to start backfill",
    nextSyncValue: "~6 hours",
    fetching: "Fetching...",
    never: "Never",
    justNow: "Just now",
    minutesAgo: "{{count}}m ago",
    hoursAgo: "{{count}}h ago",
    daysAgo: "{{count}}d ago",
    today: "Today",
    fetchOlderTransactions: "Fetch older transactions",
  },

  // Transaction review page
  review: {
    confident: "Confident",
    newMerchant: "New merchant",
    actionNeeded: "Action needed",
    uncertain: "Uncertain",
    duplicate: "Duplicate",
    possibleDup: "Possible dup",
    approveAll: "Approve All",
    date: "Date",
    merchant: "Merchant",
    account: "Account",
    amount: "Amount",
    confidence: "Conf.",
    status: "Status",
    actions: "Actions",
    uncategorized: "Uncategorized",
    required: "Required",
    selectAccount: "Select account",
    noAccount: "No account",
    investmentEnrichmentRequired: "Investment enrichment is required for this category. Please fill ticker, quantity, and price.",
    investmentEnrichmentRequiredTickerOptional: "Investment enrichment is required for this category. Please fill quantity and price; the ticker is optional for unlisted funds.",
    currencyMismatch: "This asset trades in {{assetCurrency}} but the transaction is in {{txCurrency}}. Portfolio values may require currency conversion.",
    detailsNotes: "Details / Notes",
    optionalNotes: "Optional notes override",
    tags: "Tags",
    skip: "Skip",
    merchantHistory: "Merchant History",
    noMerchantHistory: "No previous transactions found for this merchant.",
    blissAnalysis: "Bliss Analysis",
    aiConfidence: "AI Confidence",
    source: "Source",
    plaidCategory: "Plaid Category",
    classificationReasoning: "Classification Reasoning",
    whyChose: "Why I chose this",
    sourceLabels: {
      USER_OVERRIDE: "Set by you",
      EXACT_MATCH: "Matched from history",
      VECTOR_MATCH: "Similar to your history",
      VECTOR_MATCH_GLOBAL: "Pattern match (community)",
      LLM: "AI classification",
      PLAID_HINT: "Suggested by bank",
      default: "AI analysis",
    },
    enrichmentRequired: "Enrichment Required",
    enrichmentDescription: "This transaction appears investment-related. Please provide the details below to properly track it in your portfolio.",
    tickerSymbol: "Ticker Symbol",
    tickerOptional: "optional",
    tickerPlaceholder: "e.g. VWCE, AAPL",
    pricePerShare: "Price per Share",
    pricePlaceholder: "e.g. 43.67",
    quantity: "Quantity",
    quantityAuto: "(auto)",
    quantityCalculated: "Calculated automatically",
    quantityPlaceholder: "e.g. 1.23",
    notesPlaceholder: "Optional notes (overrides the default transaction description)",
    selectCategory: "Please select a category",
    selectDate: "Please select a date",
    descriptionRequired: "Description is required",
    selectCurrency: "Please select a currency",
    selectAccountValidation: "Please select an account",
    // Page-level strings
    pageTitle: "Transaction Review",
    pageSubtitle: "Review and approve pending transactions from all sources.",
    requeueSkipped: "Re-queue Skipped",
    bulkPromote: "Bulk Promote",
    reviewedProgress: "{{done}} of {{total}} reviewed",
    allPending: "All Pending",
    fromPlaid: "From Plaid",
    fromImports: "From Imports",
    allCaughtUp: "All caught up!",
    noPendingTransactions: "No pending transactions to review.",
    plaidTransactions: "Plaid Transactions",
    importTransactions: "Import Transactions",
    pending: "pending",
    cancelImport: "Cancel import",
    noPlaidToReview: "No Plaid transactions to review",
    allPlaidProcessed: "All Plaid transactions have been processed.",
    noPendingImports: "No pending imports",
    allImportsCommitted: "All imports have been committed.",
    committingTransactions: "Committing Transactions...",
    complete: "complete",
    noRowsInImport: "No rows in this import.",
    committing: "Committing...",
    commitAll: "Commit All",
    // Deep dive drawer
    selectCategoryPlaceholder: "Select category",
    saveChanges: "Save Changes",
    saveAndPromote: "Save & Promote",
    resetToPending: "Reset to Pending",
    resetToPendingDesc: "Move this row back to pending so it's not auto-committed. Useful for undoing an auto-promoted classification you want to re-examine.",
    // Bulk promote dialog
    bulkPromoteTitle: "Bulk Promote Transactions",
    bulkPromoteDescription: "Automatically promote all classified Plaid transactions that exceed the confidence threshold.",
    minConfidenceThreshold: "Minimum Confidence Threshold",
    threshold90: "90% (Most conservative)",
    threshold80: "80% (Recommended)",
    threshold70: "70%",
    threshold50: "50% (Least conservative)",
    thresholdHint: "Only transactions with AI confidence at or above this threshold will be promoted.",
    promoteAll: "Promote All",
    promoting: "Promoting...",
    // Promote-by-description dialog
    promoteMatchingTitle: "Promote matching transactions?",
    promoteMatchingDescription: "Found <strong>{{count}} pending transaction{{plural}}</strong> named \"{{description}}\".",
    justThisOne: "Just this one",
    promoteAllCount: "Promote all {{count}}",
    // Drawer promote-all dialog
    confirmMatchingTitle: "Also confirm matching transactions?",
    confirmMatchingDescription: "There {{verb}} <strong>{{count}} other pending transaction{{plural}}</strong> named \"{{description}}\". Confirm them all as <strong>{{category}}</strong>?",
    confirmAllCount: "Confirm all {{count}}",
    confirming: "Confirming...",
    // Pagination
    pageOf: "Page {{current}} of {{total}}",
    // Source labels (additional)
    unknown: "Unknown",
    // Classification failure & retry
    classificationFailed: "Classification failed",
    retryClassification: "Retry classification",
    failedTransactionsBanner: "{{count}} transaction{{plural}} failed classification",
    retrySuccess: "Retry started",
    retryError: "Failed to start retry",
    errorTimedOut: "AI classification timed out — try again or categorize manually",
    errorRateLimited: "AI provider rate limit hit — try again shortly",
    errorGeneric: "AI classification failed — try again or categorize manually",
    // Smart Import hold-back reasons (#142)
    holdBack: {
      missingFields: "Missing {{fields}}",
      fieldTicker: "ticker",
      fieldQuantity: "quantity",
      fieldPrice: "price",
      missingAccount: "Missing account",
      duplicate: "Duplicate",
      updateFailed: "Update failed",
      commitError: "Commit error",
      filterLabel: "Held back only",
    },
    commitToast: {
      committedTitle: "Import committed",
      partialTitle: "Partial commit complete",
      created: "{{count}} created",
      updated: "{{count}} updated",
      noneCommitted: "No rows committed",
      heldBack: "{{count}} held back: {{reasons}}",
      missingTicker_one: "{{count}} missing ticker",
      missingTicker_other: "{{count}} missing ticker",
      missingQuantity_one: "{{count}} missing quantity",
      missingQuantity_other: "{{count}} missing quantity",
      missingPrice_one: "{{count}} missing price",
      missingPrice_other: "{{count}} missing price",
      missingAccount_one: "{{count}} missing account",
      missingAccount_other: "{{count}} missing account",
      duplicate_one: "{{count}} duplicate",
      duplicate_other: "{{count}} duplicates",
      updateFailed_one: "{{count}} failed update",
      updateFailed_other: "{{count}} failed updates",
      commitError_one: "{{count}} commit error",
      commitError_other: "{{count}} commit errors",
      stillToReview: "{{count}} still to review",
      done: "Done.",
    },
  },

  // Currency rates page
  currencyRates: {
    title: "Currency Rates",
    subtitle: "Manage currency exchange rates for financial calculations",
    refreshRates: "Refresh Rates",
    addRate: "Add Rate",
    searchPlaceholder: "Search currencies or providers...",
    from: "From",
    to: "To",
    rate: "Rate",
    year: "Year",
    month: "Month",
    day: "Day",
    provider: "Provider",
    lastUpdated: "Last Updated",
    noRatesFound: "No rates found",
    addTitle: "Add Currency Rate",
    providerOptional: "Provider (optional)",
    adding: "Adding...",
  },

  // User management page
  userManagement: {
    title: "User Management",
    subtitle: "Manage users and their access to your tenant",
    createUser: "Create User",
    createTitle: "Create a New User",
    createDescription: "Create a new user account with login credentials.",
    editTitle: "Edit User",
    editDescription: "Update user information and access permissions",
    name: "Name",
    namePlaceholder: "Enter their name",
    emailAddress: "Email Address",
    emailPlaceholder: "Enter their email address",
    password: "Password",
    passwordPlaceholder: "Set a password (min 6 characters)",
    role: "Role",
    selectRole: "Select role",
    roleAdmin: "Admin",
    roleMember: "Member",
    roleViewer: "Viewer (read-only)",
    preferredLanguage: "Preferred Language",
    selectLanguage: "Select language",
    birthDate: "Birth Date",
    cancel: "Cancel",
    updateUser: "Update User",
    tenantUsers: "Tenant Users",
    tenantUsersDescription: "Manage users with access to your financial data",
    searchUsers: "Search users...",
    statusFilter: "Status",
    allStatuses: "All Statuses",
    statusActive: "Active",
    statusPending: "Pending",
    statusInactive: "Inactive",
    showing: "Showing",
    of: "of",
    users: "users",
    user: "User",
    lastLogin: "Last Login",
    never: "Never",
    editUser: "Edit user",
    resendInvitation: "Resend invitation",
    removeUser: "Remove user",
    noUsersFound: "No users found matching the filters",
    userCreated: "User created",
    userCreatedDetail: "{{name}} has been created successfully",
    userCreateFailed: "Something went wrong when creating the user",
    userUpdated: "User updated",
    userUpdatedDetail: "{{name}} has been updated successfully",
    userUpdateFailed: "Something went wrong when updating the user",
    userRemoved: "User removed",
    userRemovedDetail: "The user has been removed from this tenant",
    userRemoveFailed: "Something went wrong when removing the user",
    invitationSent: "Invitation sent",
    invitationSentDetail: "A new invitation has been sent to {{email}}",
    invitationFailed: "Something went wrong when resending the invitation",
    relationSelf: "Self",
    relationPartner: "Partner",
    relationChild: "Child",
    relationRelative: "Other Relative",
    relationFriend: "Friend",
    relationColleague: "Colleague",
    relationOther: "Other",
  },

  // Equity analysis page
  equityAnalysis: {
    diversified: "Diversified",
    etfBadge: "ETF",
    basedOn: "based on {{pct}}% of value",
    unmapped: "{{pct}}% unmapped",
    copyBadge: "Copy",
    viaCopy: "via @{{username}}",
    viaCopyIncluded: "incl. {{value}} via @{{username}}",
    copyCash: "Cash",
    copyUnmapped: "Unidentified",
    copySplit: "{{value}} split into {{count}} positions",
    title: "Equity Analysis",
    subtitle: "Analyze your holdings by sector, industry, country and asset class",
    totalEquityValue: "Total Equity Value",
    holdings: "Holdings",
    avgPE: "Avg P/E Ratio",
    avgDividendYield: "Avg Dividend Yield",
    groupBy: "Group by:",
    sector: "Sector",
    industry: "Industry",
    country: "Country",
    allocationBy: "Allocation by {{grouping}}",
    noStockHoldings: "No stock or ETF holdings found",
    top10: "Top 10 Holdings by Weight",
    allHoldings: "All Stock & ETF Holdings",
    noHoldingsHint: "No stock or ETF holdings found. Add stocks or ETFs to your portfolio to see equity analysis.",
    symbol: "Symbol",
    name: "Name",
    pe: "P/E",
    divYield: "Div Yield",
    eps: "EPS",
    weekRange: "52W Range",
    weight: "Weight",
    value: "Value",
    loadFailed: "Failed to load equity analysis data.",
    assetClass: "Asset class",
    lookThrough: "Look through ETFs",
    lookThroughHint: "Split each ETF across the sectors and countries it holds",
    lookThroughWithCopies: "Look through ETFs and copies",
    lookThroughHintWithCopies: "Split each ETF across the sectors and countries it holds, and show each eToro copy as the holdings it is invested in",
    pendingSecurityData: "Waiting for security data for {{symbols}}. These funds appear here once it has been fetched.",
    lookThroughOther: "Other",
    fixedIncomeBucket: "Fixed Income",
    editAssetClass: "Edit asset class for {{symbol}}",
    automatic: "Automatic ({{assetClass}})",
    overrideSaved: "Asset class updated",
    overrideFailed: "Could not update the asset class",
    overrideHint: "Set manually",
    etfBreakdown: "Top sectors",
    save: "Save",
    cancel: "Cancel",
    assetClasses: {
      STOCK: "Stock",
      INDEX_ETF: "Index ETF",
      SECTOR_ETF: "Sector ETF",
      BOND_ETF: "Bond ETF",
      REIT: "REIT",
      FUND: "Fund",
      GOV_BOND: "Government bond",
      CORP_BOND: "Corporate bond",
      REAL_ESTATE: "Real estate",
      CRYPTO: "Crypto",
      CASH: "Cash",
      OTHER: "Other",
      COPY_TRADE: "Copy trade",
    },
  },

  // Not Found page
  notFoundPage: {
    code: "404",
    message: "Oops! Page not found.",
    returnHome: "Return to Home",
  },

  // Plaid connect
  plaidConnect: {
    success: "Success",
    bankReauthenticated: "Bank connection re-authenticated successfully.",
    reauthFailed: "Re-authentication succeeded but failed to update status.",
    exchangeFailed: "Exchange failed",
    connectFailed: "Failed to connect bank account.",
    reconnect: "Reconnect",
    connectBank: "Connect Bank",
  },

  // Account selection modal
  accountSelection: {
    loadFailed: "Failed to load accounts from bank.",
    allAccountTypes: "All account types",
    createNew: "Create new",
    createNewAccount: "Create new account",
    linkToExisting: "Link to existing",
    selectOrCreate: "Select or create",
    accountNames: "Account names",
    accountMappings: "Account mappings",
    unsupportedCurrencies: "Unsupported currencies",
    unsupportedCountry: "Unsupported country",
    seedInterview: "Seed interview",
    allCategories: "All categories",
    fetchFailed: "Failed to fetch Plaid accounts",
    setupTitle: "Set Up Accounts",
    syncingTitle: "Syncing in Progress...",
    quickClassify: "Quick Classify",
    syncComplete: "Sync Complete",
    setupDescription: "Choose accounts to sync, link to existing ones, and set names.",
    syncingDescription: "Your transactions are being synced and classified by AI.",
    seedDescription: "We found your most recurring transactions. Confirm or adjust their categories to make AI smarter for your next sync.",
    doneDescription: "All transactions have been synced and classified.",
    newCurrenciesDetected: "New currencies detected",
    currenciesUse: "These accounts use",
    currenciesNotConfigured: "which {{plurality}} not yet in your workspace. They will be added automatically when you sync.",
    newCountryDetected: "New country detected",
    bankOperatesIn: "This bank operates in",
    countryNotConfigured: "which is not yet in your workspace. It will be added automatically when you sync.",
    notConfigured: "not configured",
    accountNamePlaceholder: "Account name",
    existingAccountKept: "Existing account name will be kept",
    noAccountsFound: "No accounts found.",
    syncHistoryNote: "Bliss will sync up to 2 years of history. Recent transactions appear in seconds; full history arrives within 24 hours.",
    syncAccounts: "Sync {{count}} Accounts",
    accountsLinked: "Accounts linked",
    syncingTransactions: "Syncing transactions",
    aiClassifying: "AI classifying",
    importingTransactions: "Importing your recent transactions",
    importingFrom: "from",
    fullHistoryNote: "Your full 2-year history will continue to sync in the background.",
    promoted: "Promoted",
    pendingReview: "Pending Review",
    processing: "Processing",
    doNotClose: "Please don't close this dialog while syncing is in progress.",
    skipForNow: "Skip for now",
    confirmContinue: "Confirm & Continue",
    seedSaveFailed: "Could not save category selections.",
    historyRange: "History Range",
    readyForReview: "Ready for Review",
    skippedDuplicates: "Skipped (duplicates)",
    fullHistorySyncing: "Full history syncing in the background",
    currentlySyncedFrom: "Currently synced from",
    fullHistoryDetail: "Bliss will automatically sync up to 2 years of transaction history over the next 24 hours. New transactions will appear in your review queue as they arrive — no action needed.",
    reviewTransactions: "Review Transactions",
    linkFailed: "Failed to link accounts.",
  },

  // Change password dialog
  changePassword: {
    title: "Change Password",
    description: "Enter your current password and choose a new one.",
    currentPassword: "Current Password",
    currentPasswordRequired: "Current password is required",
    newPassword: "New Password",
    newPasswordMin: "Password must be at least 8 characters",
    minHint: "Minimum 8 characters",
    confirmPassword: "Confirm New Password",
    confirmRequired: "Please confirm your new password",
    mismatch: "Passwords do not match",
    updatePassword: "Update Password",
    passwordUpdated: "Password updated",
    passwordUpdatedDetail: "Your password has been changed successfully.",
    changeFailed: "Failed to change password",
  },

  // Transaction form
  transactionFormPage: {
    pickDate: "Pick a date",
    descriptionPlaceholder: "e.g., Groceries, Salary, Rent",
    selectAccount: "Please select an account",
    selectCurrency: "Please select a currency",
    selectCategory: "Please select a category",
    pricePerShare: "Price per Share",
    pricePlaceholder: "e.g. 43.67",
    quantity: "Quantity",
    quantityAuto: "(auto)",
    quantityCalculated: "Calculated automatically",
    quantityPlaceholder: "e.g. 1.23",
    detailsNotes: "Details / Notes",
    notesPlaceholder: "Optional notes (overrides the default transaction description)",
    debtTerms: "Debt Terms",
    initialBalance: "Initial Balance",
    interestRate: "Interest Rate (%)",
    termMonths: "Term (in months)",
    originationDate: "Origination Date",
    tags: "Tags",
    saving: "Saving...",
    updatedSuccess: "Transaction updated successfully.",
    createdSuccess: "Transaction created successfully.",
    saveFailed: "Failed to save transaction. Please try again.",
    currencyMismatch: "This asset trades in {{currency}}. Please select a {{currency}} account.",
    currencyMismatchAlt: "Please select a {{currency}} account or choose an asset that trades in {{txCurrency}}.",
    tickerRequired: "Ticker is required for this category.",
    priceRequired: "Price is required for this category.",
    quantityRequired: "Quantity is required for this category.",
    dateLabel: "Date",
    descriptionLabel: "Description",
    accountLabel: "Account",
    creditLabel: "Credit",
    debitLabel: "Debit",
    categoryLabel: "Category",
    investmentDetails: "Investment Details",
    investmentDetailsHint: "(select an investment category to enable)",
    tickerLabel: "Ticker",
    tickerPlaceholder: "e.g., AAPL, VWCE",
    etoroUsernameLabel: "eToro username",
    etoroUsernamePlaceholder: "Copied investor, e.g. topinvestor",
    copyTradeAmountHint: "Enter the amount you put into (or took out of) the copy. There is no price or quantity: the amount is your cost and eToro supplies the value.",
    loadingFormData: "Loading form data...",
    closePosition: "Close full position",
    closePositionHint: "Sets quantity to your exact holding of {{quantity}} units",
    closePositionActive: "Position close active",
    lotMismatchWarning: "Position quantity mismatch",
    lotMismatchDetail: "This position shows a negative quantity — you may have a missing buy transaction or an unrecorded cross-account share transfer.",
  },

  // Debt terms form
  debtTermsForm: {
    initialBalance: "Initial Balance",
    initialBalanceError: "Initial balance must be a positive number.",
    interestRate: "Interest Rate (%)",
    interestRateNegative: "Interest rate cannot be negative.",
    interestRateTooHigh: "Interest rate seems too high.",
    termMonths: "Term (in months)",
    termError: "Term must be a positive number of months.",
    originationDate: "Origination Date",
    originationRequired: "An origination date is required.",
    pickDate: "Pick a date",
    saveTerms: "Save Terms",
    saving: "Saving...",
    success: "Success!",
    savedDetail: "Debt terms for {{name}} have been saved.",
    saveFailed: "Failed to save debt terms. Please try again.",
  },

  // Manual price form
  manualPriceForm: {
    date: "Date",
    pickDate: "Pick a date",
    price: "Price",
    pricePlaceholder: "e.g., 150.50",
    currency: "Currency",
    currencyPlaceholder: "e.g., USD",
    notes: "Notes (Optional)",
    notesPlaceholder: "e.g., Source of valuation",
    savePrice: "Save Price",
    saving: "Saving...",
    success: "Success!",
    savedDetail: "Price for {{name}} has been updated.",
    saveFailed: "Failed to update price. Please try again.",
  },

  manualPriceHistory: {
    title: "Price history — {{symbol}}",
    dialogDescription: "Review, edit or remove the prices you have recorded by hand for this asset.",
    addTitle: "Record a price — {{symbol}}",
    editTitle: "Edit price — {{symbol}}",
    countLabel_one: "{{count}} update",
    countLabel_other: "{{count}} updates",
    countLabel: "{{count}} updates",
    effectiveDate: "Effective date",
    price: "Price",
    currency: "Currency",
    notes: "Notes",
    recordedOn: "Recorded on",
    actions: "Actions",
    edit: "Edit",
    delete: "Delete",
    back: "Back to history",
    differentCurrency: "This entry is stored in a different currency from the asset.",
    emptyTitle: "No manual prices recorded yet",
    emptyBody: "Record the first price for this asset to start building its history.",
    recordFirst: "Record first price",
    recordPrice: "Record price",
    loadError: "Failed to load price history.",
    retry: "Retry",
    deleteConfirmTitle: "Delete this price entry?",
    deleteConfirmBody: "This permanently removes the recorded price and re-triggers portfolio valuation. This cannot be undone.",
    deleted: "Price entry deleted",
    deleteFailed: "Failed to delete price entry. Please try again.",
    viewHistory: "View history",
    lastUpdated: "Last updated",
    allManualAssetsTitle: "All manually-priced assets",
    allManualAssetsDesc: "Every asset you price by hand. Open its history to review, edit, or remove past entries.",
    noManualAssets: "No manually-priced assets yet.",
    pagination: "Showing {{from}}–{{to}} of {{total}}",
    prev: "Previous",
    next: "Next",
  },

  // Category combobox
  categoryCombobox: {
    search: "Search categories...",
    noResults: "No categories found.",
  },

  // Category form
  categoryFormPage: {
    nameMin: "Name must be at least 2 characters",
    typeRequired: "Type is required",
    groupRequired: "Group is required",
    iconLabel: "Icon",
    iconHint: "Paste any emoji to use as the category icon.",
    recurringLabel: "Recurring charge",
    recurringHint: "Show this category's transactions in the Subscriptions view. A single charge is enough to list it.",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Briefly explain when to use this category…",
    descriptionHint: "Shown as a tooltip in the category picker. Helps you (or others on your tenant) pick the right category.",
    namePlaceholder: "e.g. Groceries, Salary, Rent",
    selectType: "Select type",
    selectGroup: "Select group",
    selectTypeFirst: "Select a type first",
    createNewGroup: "+ Create new group\u2026",
    enterNewGroup: "Enter new group name",
    iconOptional: "(optional)",
  },

  // Categories page
  categoriesPage: {
    defaultBadge: "Default",
    systemHint: "System category — used for AI classification across all tenants. Cannot be deleted.",
    transaction: "transaction",
    transactions: "transactions",
    categoryOptions: "Category options",
    rename: "Rename",
    addTo: "Add to {{type}}",
    reassignAndDelete: "Reassign & Delete",
    reassignMessage: "\"{{name}}\" has {{count}} transaction(s). Choose a category to reassign them to before deleting.",
    selectTargetCategory: "Select target category",
    deleteAndReassign: "Delete & Reassign",
    deletedAndReassigned: "{{name}} deleted and {{count}} transaction(s) reassigned",
  },

  // Expense split card (dashboard)
  expenseSplit: {
    title: "Expense Split",
    thisYear: "This year",
    thisMonth: "This month",
    lastMonth: "Last month",
    last3Months: "Last 3 months",
    noData: "No spending data for this period.",
    viewExpenses: "View expenses",
    ofSpending: "of spending",
    basis: "Debits − credits",
    netCredits: "Net credits",
    noNetSpend: "No net spend in this period",
  },

  // Smart Import page
  smartImport: {
    title: "Smart Import",
    subtitle: "Upload a CSV or Excel file, let AI classify your transactions, then review and commit.",
    cancelImport: "Cancel Import",
    steps: {
      upload: "Upload",
      processing: "Processing",
      review: "Review",
      done: "Done",
    },
    status: {
      confirmed: "Confirmed",
      pending: "Pending",
      duplicate: "Duplicate",
      possibleDup: "Possible Dup",
      skipped: "Skipped",
      error: "Error",
    },
    source: {
      manual: "Manual",
      exact: "Exact",
      vector: "Vector",
      ai: "AI",
    },
    selectFile: "Select File",
    selectFileDesc: "Choose a bank statement or export file (CSV or Excel) to import.",
    dropzone: "Click to select or drag a CSV or Excel file",
    detectingFormat: "Detecting file format...",
    formatDetected: "Format Detected: {{name}}",
    formatDetectedDesc: "This file matches the {{name}} adapter. Column mapping will be applied automatically.",
    unknownFormat: "Unknown Format",
    unknownFormatDesc: "No adapter matches this file. Headers detected:",
    unknownFormatHeaderHint: "Bliss reads column headers from the first row of the file (or of the sheet, for Excel). If your file has lines above the headers — account name, period, balance — delete them and upload it again.",
    createAdapterForFormat: "Create Adapter for this Format",
    destinationAccount: "Destination Account",
    destinationAccountDesc: "All transactions will be imported into this account.",
    destinationAccountNative: "Optional — the native adapter resolves account per-row from your CSV.",
    selectAccount: "Select an account...",
    optionalFromCsv: "Optional — resolved from CSV...",
    nativeAccountHint: "Each row's account column overrides this selection. Only select a fallback here if some rows have no account specified.",
    uploading: "Uploading...",
    uploadAndProcess: "Upload & Process",
    importAdapters: "Import Adapters",
    hide: "Hide",
    manage: "Manage",
    noAdapters: "No adapters yet. Create one to start importing.",
    newAdapter: "New Adapter",
    system: "System",
    template: "Template",
    editAdapter: "Edit Adapter",
    createAdapter: "Create Adapter",
    adapterFormDesc: "Define how your bank's CSV/Excel columns map to Bliss transaction fields.",
    form: {
      name: "Name",
      nameRequired: "Adapter name is required",
      namePlaceholder: "Chase Checking CSV",
      matchHeaders: "Match Headers",
      matchHeadersPlaceholder: "Date, Description, Amount",
      matchHeadersHint: "Column headers that uniquely identify this bank's export format.",
      matchHeadersChipsHint: "Toggle the headers from your file that identify this format. Add a custom header for columns not listed.",
      addCustomHeader: "Add custom header",
      dateColumn: "Date Column",
      dateColumnPlaceholder: "Date",
      descriptionColumn: "Description Column",
      descriptionColumnPlaceholder: "Description",
      amountStrategy: "Amount Layout",
      singleSigned: "One column, signed (most banks)",
      singleSignedDesc: "e.g. -45.20 = expense, 25.00 = income",
      singleSignedInverted: "One column, inverted signs (e.g. Amex)",
      singleSignedInvertedDesc: "e.g. 45.20 = expense, -25.00 = refund",
      debitCredit: "Separate Debit & Credit columns",
      debitCreditDesc: "Two separate columns for charges and credits",
      amountWithType: "Amount + Type column",
      amountWithTypeDesc: "One amount column plus a column indicating direction",
      typeColumn: "Type Column",
      typeColumnPlaceholder: "Type",
      categoryColumn: "Category Column (optional)",
      categoryColumnPlaceholder: "None — skip category hint",
      categoryColumnHint: "If your file has a category column, mapping it improves AI classification accuracy",
      debitColumn: "Debit Column",
      debitColumnPlaceholder: "Debit",
      creditColumn: "Credit Column",
      creditColumnPlaceholder: "Credit",
      amountColumn: "Amount Column",
      amountColumnPlaceholder: "Amount",
      customColumn: "Custom column…",
      dateFormat: "Date Format",
      dateFormatAuto: "Auto-detect (recommended)",
      dateFormatCustom: "Custom format…",
      dateFormatGroupDate: "Date only",
      dateFormatGroupDateTime: "Date + Time",
      dateFormatAmbiguousWarning: "Date format is ambiguous (all values ≤ 12). Select a format explicitly to avoid MM/DD vs DD/MM errors.",
      dateFormatHint: "Pick a preset or choose Custom to enter your own",
      defaultCurrency: "Default Currency",
      currencyHint: "Leave blank if your CSV has a per-row currency column",
      currencyNone: "None — use per-row currency column",
      skipRows: "Skip Rows (header rows to skip)",
      previewTitle: "Row 1 preview",
      previewHint: "Fill in the columns above to see how row 1 will be parsed",
      previewNoData: "Upload a file first to enable preview",
      previewDate: "Date",
      previewDescription: "Description",
      previewAmount: "Amount",
      previewCurrency: "Currency",
      previewBankCategory: "Bank Category",
    },
    unknownFormatSamplePreview: "Sample rows from your file:",
    processingTitle: "Processing Your Import",
    processingDesc: "AI is classifying your transactions. This may take a moment...",
    percentComplete: "{{pct}}% complete",
    unknownAccount: "Unknown Account",
    quickClassify: "Quick Classify",
    quickClassifyDesc: "We found your most recurring transactions. Confirm or adjust their categories to make AI smarter for future imports.",
    skipForNow: "Skip for now",
    confirmAndContinue: "Confirm & Continue",
    confirmAll: "Confirm All",
    seed: {
      global: "Global",
      match: "Match",
      restore: "Restore",
      skipMerchant: "Skip this merchant",
    },
    review: {
      file: "File",
      account: "Account",
      total: "Total",
      nRows: "{{count}} rows",
      earliest: "Earliest",
      autoConfirmed: "{{count}} auto-confirmed",
      nUpdates: "{{count}} updates",
      pendingReview: "{{count}} pending review",
      nDuplicates: "{{count}} duplicates",
      nErrors: "{{count}} errors",
      filterByStatus: "Filter by status",
      flat: "Flat",
      grouped: "Grouped",
      errorLoadingTitle: "Error Loading Import",
      errorLoadingDesc: "Could not load the staged import data. Please try refreshing.",
      noRowsFound: "No rows found",
      tryDifferentFilter: "Try a different filter.",
      noRowsProduced: "The import did not produce any rows.",
      pageOf: "Page {{current}} of {{total}}",
    },
    filter: {
      allRows: "All Rows",
      possibleDuplicate: "Possible Duplicate",
    },
    committingTitle: "Committing Transactions",
    committingDesc: "Creating transactions from your confirmed rows...",
    committing: "Committing...",
    commitImport: "Commit Confirmed",
    importError: "Import Error",
    processingError: "An error occurred during processing.",
    processingFailed: "Processing failed",
    startOver: "Start Over",
    importComplete: "Import Complete!",
    importCompleteDesc: "Your transactions have been committed successfully.",
    done: {
      created: "Created",
      updated: "Updated",
      remaining: "Remaining",
    },
    importAnotherFile: "Import Another File",
    reviewInTransactionReview: "Review in Transaction Review",
    commitDialogDesc: "This will create transactions from your confirmed rows only. Pending rows will remain staged for future review. This action cannot be undone.",
    goBack: "Go Back",
    yesCommit: "Yes, Commit",
    cancelDialogDesc: "Are you sure you want to cancel? All staged rows will be discarded.",
    cancelling: "Cancelling...",
    yesCancelImport: "Yes, Cancel Import",
    toast: {
      importReady: "Import Ready",
      rowsReady: "{{count}} rows ready for review.",
      seedSaveFailed: "Could not save category selections.",
      detectionFailed: "Detection Failed",
      detectionFailedDesc: "Could not detect the file format. Please check your CSV or Excel file.",
      uploadFailed: "Upload Failed",
      uploadFailedDesc: "Could not start the import. Please try again.",
      otherTransactions: "{{count}} other transactions in \"{{category}}\"",
      confirmAllQuestion: "Confirm all?",
      confirmedN: "Confirmed {{count}} transactions",
      commitStarted: "Commit Started",
      commitStartedDesc: "Your transactions are being committed...",
      commitFailed: "Commit Failed",
      commitFailedDesc: "Could not start the commit process.",
      importCancelled: "Import Cancelled",
      cancelFailed: "Cancel Failed",
      adapterUpdated: "Adapter updated",
      adapterCreated: "Adapter created",
      adapterDeleted: "Adapter deleted",
      formatMatched: "Format matched: {{name}}",
      formatMatchedDesc: "Your file is now recognized. You can proceed with the upload.",
      updateFailed: "Update failed",
      createFailed: "Create failed",
      deleteFailed: "Delete failed",
    },
  },

  // Insights page
  insights: {
    title: "Insights",
    subtitle: "AI-powered observations about your finances",
    v1Subtitle: "AI-powered financial intelligence: monthly reviews, quarterly trends, annual recaps, and portfolio analysis",
    lastUpdated: "Last updated",
    generating: "Generating...",
    generateNew: "Generate Insights",
    generate: "Generate Insights",
    generateAll: "Generate all tiers",
    refresh: "Refresh",
    refreshTier: "Regenerate",
    moreActions: "More actions",
    reload: "Reload",
    clearFilters: "Clear filters",
    noInsightsYet: "No insights yet",
    noInsightsDesc: "Once you have transaction data, Bliss will analyze your finances and surface patterns that matter.",
    noCategoryInsights: "No insights match the selected categories for this period.",
    noTierInsightsDesc: "No insights have been generated for this tier yet. Use Regenerate to create one for the latest period.",
    period: {
      selectPlaceholder: "Select period",
      q: "Q",
    },
    severity: {
      POSITIVE: "Positive",
      INFO: "Info",
      WARNING: "Warning",
      CRITICAL: "Critical",
    },
    categories: {
      all: "All",
      spending: "Spending",
      income: "Income",
      savings: "Savings",
      portfolio: "Portfolio",
      debt: "Debt",
      netWorth: "Net Worth",
    },
    tiers: {
      monthly: "Monthly Review",
      quarterly: "Quarterly Deep Dive",
      annual: "Annual Report",
      portfolio: "Portfolio Intelligence",
    },
    lens: {
      passiveIncomeOutlook: "Passive income outlook",
    },
    actions: {
      passiveIncomeSetup: "Set up income terms",
      portfolioHoldings: "Portfolio holdings",
    },
  },

  // Manual Updates page
  manualUpdates: {
    pageTitle: "Manual Updates & Terms",
    pageSubtitle: "Keep your manual assets and liabilities up to date.",
    errorLoading: "Failed to load asset data. Please try again later.",
    stalePricesTitle: "Action Required: Stale Prices",
    stalePricesDesc: "These manually-tracked assets haven't been updated in over 30 days.",
    asset: "Asset",
    value: "Value",
    status: "Status",
    action: "Action",
    never: "Never",
    updatedOn: "Updated {{date}}",
    dOld: "{{count}}d old",
    updatePrice: "Update Price",
    allUpToDate: "All manually-tracked assets are up to date.",
    liabilityTerms: "Liability Terms",
    liabilityTermsDesc: "Manage the terms for your manually tracked loans and credit lines.",
    debtName: "Debt Name",
    principalBalance: "Principal Balance",
    interestRate: "Interest Rate",
    duration: "Duration",
    yr: "yr",
    mo: "mo",
    editTerms: "Edit Terms",
    addTerms: "Add Terms",
    totalOutstanding: "Total Outstanding",
    noLiabilities: "No liabilities found.",
    updatePriceDialog: "Update Price",
    debtTermsDialog: "Debt Terms",
    urgency: {
      critical: "Critical",
      warning: "Warning",
      stale: "Stale",
    },
  },

  // Spending category chart
  spendingChart: {
    title: "Spending by Category",
    selectPeriod: "Select period",
    thisMonth: "This month",
    lastMonth: "Last month",
    last3Months: "Last 3 months",
    noData: "No spending data for this period.",
  },

  // Portfolio holdings page
  portfolio: {
    title: "Portfolio Holdings",
    subtitle: "Track your investment portfolio performance and allocation",
    assets: "Assets",
    liabilities: "Liabilities",
    positionCount_one: "{{count}} position",
    positionCount_other: "{{count}} positions",
    positionCount: "{{count}} positions",
    symbol: "Symbol",
    qty: "Qty",
    price: "Price",
    costBasis: "Cost Basis",
    unrealizedPnL: "Unrealized P&L",
    realizedPnL: "Realized P&L",
    roiPercent: "ROI %",
    totalValue: "Total Value",
    roiTooltip: "Total ROI = (Realized + Unrealized P&L) / Total Invested",
    showDebt: "Show Debt",
    allAccounts: "All accounts",
    allCountries: "All countries",
    noHistoricalData: "No historical data available for this period.",
    // Holding filter (#131)
    allHoldings: "All holdings",
    searchHoldings: "Search holdings…",
    noHoldingsFound: "No holdings found.",
    showClosedPositionsToggle: "Show closed positions",
    closedBadge: "Closed",
    closedPositionsGroup: "Closed positions",
    clearHolding: "Clear holding filter",
    showInChart: "Show in chart",
    holdingNotInAccount: "{{symbol}} isn't held in {{account}}",
    noHistoryForHolding: "No history for {{name}} in this range",
    holdingPickerLabel: "Filter the chart by holding",
    netWorth: "Net Worth",
    emptyState: "No portfolio data yet. Connect a bank account or import transactions to get started.",
    connectAccount: "Connect Account",
    importCSV: "Import CSV",
    errorTitle: "Error Loading Portfolio",
    errorDescription: "There was an issue fetching your portfolio data. Please try again later.",
    liability: "Liability",
    interestRate: "Interest Rate",
    term: "Term",
    balance: "Balance",
    totalOutstanding: "Total Outstanding",
    editTerms: "Edit Terms",
    addTerms: "Add Terms",
    hideClosedPositions_one: "Hide {{count}} closed position",
    hideClosedPositions_other: "Hide {{count}} closed positions",
    hideClosedPositions: "Hide {{count}} closed positions",
    showClosedPositions_one: "Show {{count}} closed position",
    showClosedPositions_other: "Show {{count}} closed positions",
    showClosedPositions: "Show {{count}} closed positions",
  },

  // Tag analytics page
  tagAnalytics: {
    title: "Tag Analytics",
    subtitle: "Analyze spending by tag across all accounts and currencies",
    selectTag: "Select a tag...",
    compare: "Compare",
    compareWith: "Compare with...",
    searchTags: "Search tags...",
    noTagsFound: "No tags found.",
    emptyTitle: "Select a tag to view analytics",
    emptyDescription: "Choose a tag above to see spending breakdowns, trends, and budget tracking",
    totalDebit: "Total Debit",
    totalCredit: "Total Credit",
    balance: "Balance",
    budgetProgress: "Budget Progress",
    topCategory: "Top Category",
    categoryBreakdown: "Category Breakdown",
    noSpendingData: "No spending data for this tag",
    cashFlowTimeline: "Cash Flow Timeline",
    noTimelineData: "No timeline data",
    monthly: "Monthly",
  },

  // Auth page — flat string keys (components call t("literal string"))
  "Sign In": "Sign In",
  "Sign Up": "Sign Up",
  "Sign in with Google": "Sign in with Google",

  // Auth rejection messages (task #71 — identity hygiene)
  "An account with this email already exists. Sign in with your password instead.": "An account with this email already exists. Sign in with your password instead.",
  "Your Google account's email address is not verified. Verify it with Google, then try again.": "Your Google account's email address is not verified. Verify it with Google, then try again.",
  "Sign-in with Google failed. Please try again.": "Sign-in with Google failed. Please try again.",
  "We could not sign you in automatically. Please try signing in with your password.": "We could not sign you in automatically. Please try signing in with your password.",

  // Invite-only sign-up (task #99)
  "This Bliss instance is invite-only. Use the email address you were invited with.": "This Bliss instance is invite-only. Use the email address you were invited with.",
  "This email hasn't been invited to this Bliss instance. Ask the person who runs it for an invite.": "This email hasn't been invited to this Bliss instance. Ask the person who runs it for an invite.",
  "Sign up with Google": "Sign up with Google",
  "or continue with email": "or continue with email",
  "Email address": "Email address",
  "Password": "Password",
  "Signing in...": "Signing in...",
  "Login failed": "Login failed",
  "Full name": "Full name",
  "Use at least 8 characters, including a number.": "Use at least 8 characters, including a number.",
  "By creating an account you agree to our": "By creating an account you agree to our",
  "and": "and",
  "Registration failed": "Registration failed",
  "Creating account...": "Creating account...",
  "Create Account": "Create Account",
  "Don't have an account?": "Don't have an account?",
  "Create one": "Create one",
  "Already have an account?": "Already have an account?",
  "Sign in": "Sign in",
  "Welcome to the Bliss demo. Use the credentials below to sign in and explore.": "Welcome to the Bliss demo. Use the credentials below to sign in and explore.",
  "The quiet intelligence behind your global wealth.": "The quiet intelligence behind your global wealth.",
  "Financial clarity, without borders.": "Financial clarity, without borders.",
  "Bliss capybara in a turtleneck, seated in a mid-century chair beside a stack of currency coins": "Bliss capybara in a turtleneck, seated in a mid-century chair beside a stack of currency coins",
  "Bliss capybara mascot seated in a mid-century chair": "Bliss capybara mascot seated in a mid-century chair",
  "Welcome back": "Welcome back",
  "Protected by enterprise-grade encryption.": "Protected by enterprise-grade encryption.",

  // Dashboard actions — flat string keys
  "Fix Bank Connection": "Fix Bank Connection",
  "A bank connection needs your attention": "A bank connection needs your attention",
  "Review Pending Transactions": "Review Pending Transactions",
  "Check and categorize imported transactions": "Check and categorize imported transactions",
  "Update Asset Prices": "Update Asset Prices",
  "Some manual assets need a price refresh": "Some manual assets need a price refresh",
  "Connect your bank": "Connect your bank",
  "Plaid (US, CA, UK) or Enable Banking (EU) for automatic sync": "Plaid (US, CA, UK) or Enable Banking (EU) for automatic sync",
  "Add Manual Account": "Add Manual Account",
  "Manually track an account or asset": "Manually track an account or asset",
  "Import Transactions": "Import Transactions",
  "Upload a CSV file to import transactions": "Upload a CSV file to import transactions",
  "View Insights": "View Insights",
  "See AI-generated financial observations": "See AI-generated financial observations",
  "Explore Expenses": "Explore Expenses",
  "See how your spending breaks down": "See how your spending breaks down",
  "View Financial Summary": "View Financial Summary",
  "View income vs expenses over time": "View income vs expenses over time",
  "View Accounts": "View Accounts",
  "See all your connected accounts": "See all your connected accounts",

  // Setup checklist — flat string keys
  "Get started with Bliss": "Get started with Bliss",
  "of": "of",
  "complete": "complete",
  "Dismiss": "Dismiss",
  // ─── Subscriptions / recurring charges ───────────────────────────────────────
  subscriptions: {
    title: "Subscriptions",
    subtitle: "Recurring charges detected across your accounts.",
    scanNow: "Scan now",
    scanCooldownShort: "Scan in {{count}} min",
    scanStarted: "Scan started",
    scanStartedHint: "Detection is running in the background. Results appear shortly.",
    scanCooldown: "Scan on cooldown",
    scanCooldownHint: "You can run another scan in about {{count}} minutes.",
    actionFailed: "Action failed",
    fullScanHintPrefix: "Older annual subscriptions may be missing —",
    fullScanHintLink: "run a full history scan",
    fullScanHintSuffix: " in Settings → Maintenance.",
    loadError: "Could not load subscriptions.",
    uncategorized: "Uncategorized",
    toggleCharges: "Show underlying charges",
    noCharges: "No charges found for this merchant.",
    fxUnavailable: "FX unavailable",
    occurrences_one: "{{count}} charge",
    occurrences_other: "{{count}} charges",
    confirm: "Confirm",
    confirmed: "Marked as a subscription",
    notASubscription: "Not a subscription",
    dismissed: "Removed from subscriptions",
    remove: "Remove",
    removed: "Removed",
    restore: "Restore",
    restored: "Restored",
    summary: {
      monthly: "Monthly",
      annual: "Annualized",
      count: "Active subscriptions",
      fxExcluded: "{{count}} excluded (no exchange rate)",
    },
    filter: {
      active: "Active",
      lapsed: "Lapsed",
      all: "All",
      allCategories: "All categories",
    },
    empty: {
      title: "No recurring charges yet",
      body: "Once a merchant charges you a few times, it will show up here.",
    },
    status: {
      active: "Active",
      lapsed: "Lapsed",
    },
    cadence: {
      WEEKLY: "Weekly",
      MONTHLY: "Monthly",
      QUARTERLY: "Quarterly",
      ANNUAL: "Annual",
      unknown: "Unknown",
    },
    due: {
      today: "due today",
      inDays: "due in {{count}}d",
      agoDays: "{{count}}d overdue",
    },
    rename: {
      label: "Rename subscription",
      renamed: "Subscription renamed",
      custom: "custom name",
    },
    merge: {
      mergeInto: "Merge into another subscription",
      pickTarget: "Choose a subscription…",
      merged: "Subscriptions merged",
      unmerge: "Unmerge",
      unmerged: "Merge undone",
      tombstoneHint: "Folded into another subscription — its charges count toward that one.",
      unknownTarget: "another subscription",
      mergedCountHint_one: "{{count}} merged into another — manage",
      mergedCountHint_other: "{{count}} merged into others — manage",
      targetMissing: "Merge target missing",
      stale: "This merge isn't taking effect — try Unmerge and merge again.",
      dismissBlocked: "Unmerge the rows folded into this one first.",
    },
    split: {
      splitByAmount: "Split by amount — one subscription per price",
      split: "Split into one subscription per amount",
      unsplit: "Undo split",
      unsplitDone: "Split undone",
      markerHint: "Split by amount — each recurring price is its own subscription.",
      nothingToSplit: "These charges don't repeat at two or more different amounts.",
    },
    pager: {
      prev: "Previous",
      next: "Next",
      pageOf: "Page {{page}} of {{total}}",
    },
  },

  // Passive Income (#77)
  passiveIncome: {
    title: "Passive Income",
    subtitle: "Projected dividends, coupons, rent, interest and benefits next to what you actually received",
    loadFailed: "Failed to load passive income data.",
    horizon: "Projection horizon",
    months_one: "{{count}} month",
    months_other: "{{count}} months",
    kpi: {
      next12m: "Next 12 months",
      split: "{{investment}} investments · {{other}} other",
      monthlyAverage: "Monthly average",
      yieldOnValue: "Yield on value",
      yieldHint: "Investment income only",
      essentialsCoverage: "Essentials covered",
      essentialsHint: "of {{amount}} spent in the last 12 months",
      noEssentials: "No essential spending recorded",
      dataCoverage: "Data coverage",
      dataCoverageValue: "{{configured}} of {{total}}",
      dataCoverageHint: "income-producing holdings configured",
    },
    chart: {
      title: "Actual vs projected income",
      actual: "Actual",
      today: "Today",
    },
    bucket: {
      dividend: "Dividends",
      coupon: "Coupons",
      rent: "Rent",
      interest: "Interest",
      other: "Allowance & benefits",
    },
    frequency: {
      WEEKLY: "Weekly",
      MONTHLY: "Monthly",
      QUARTERLY: "Quarterly",
      SEMIANNUAL: "Semiannual",
      ANNUAL: "Annual",
      AT_MATURITY: "At maturity",
      IRREGULAR: "Irregular",
      MIXED: "Mixed",
      NONE: "No payments",
    },
    incomeType: {
      DIVIDEND: "Dividend",
      FIXED_COUPON: "Fixed-rate bond",
      FLOATING_COUPON: "Floating-rate bond",
      INFLATION_LINKED: "Inflation-linked bond",
      RENT: "Rent",
      INTEREST: "Interest",
      CUSTOM_YIELD: "Yield",
      FIXED_AMOUNT: "Fixed amount",
      NONE: "No income",
    },
    source: {
      AUTO: "Auto",
      OVERRIDE: "Override",
      MANUAL: "Manual",
      MISSING: "Missing",
      MIXED: "Mixed",
    },
    status: {
      OK: "OK",
      MATURED_UNREDEEMED: "Matured: record redemption",
      ENDED: "Ended",
      STALE_RATE: "Rate may be stale",
    },
    yearly: {
      title: "Annual summary",
      source: "Source",
      year: "Year {{n}}",
      total: "Total",
    },
    upcoming: {
      title: "Upcoming payments",
      empty: "No payments expected yet.",
    },
    breakdown: {
      filters: {
        all: "All",
        assets: "Holdings",
        streams: "Streams",
        attention: "Needs attention",
      },
      search: "Search holdings or streams",
      filter: "Filter",
      noMatches: "Nothing matches your search.",
      showing: "{{from}}–{{to}} of {{total}}",
      prev: "Previous page",
      next: "Next page",
      title: "Breakdown by holding or stream",
      description: "Projected over the next {{count}} months",
      empty: "No income configured yet. Add income terms to your holdings or an income stream.",
      holding: "Holding / stream",
      type: "Type",
      rateOrAmount: "Rate / amount",
      frequency: "Frequency",
      nextPayment: "Next payment",
      endDate: "Ends",
      horizonTotal: "Projected",
      mixed: "Mixed",
      accounts_one: "{{count}} account",
      accounts_other: "{{count}} accounts",
      showAccounts_one: "Show {{count}} account",
      showAccounts_other: "Show {{count}} accounts",
      hideAccounts: "Hide accounts",
      editGroup: "Edit income terms for every holding of {{name}}",
      cashSummary: "Balance {{balance}} · {{monthly}}/month",
      quantity: "{{value}} units",
      view: {
        label: "Breakdown view",
        grouped: "By holding",
        flat: "By account",
      },
    },
    ladder: {
      title: "Bond maturity ladder",
      description: "Principal returning per year (not counted as income)",
      empty: "No bonds mature within this horizon.",
    },
    streams: {
      title: "Other income streams",
      description: "Allowances and government benefits that aren't tied to an asset",
      add: "Add stream",
      empty: "No income streams yet.",
      noCategories: "Create an Allowance or Government Welfare category in the Passive Income group to add streams.",
    },
    missing: {
      showAll: "Show all ({{count}})",
      showLess: "Show less",
      title_one: "{{count}} holding has no income data",
      title_other: "{{count}} holdings have no income data",
      description: "Add their income terms, or mark them as not distributing, to complete the projection.",
    },
    detached: {
      searchAsset: "Search holdings…",
      noAssets: "No holdings without income terms.",
      title: "Detached income terms",
      description: "These terms belonged to holdings that were re-created after a corrected import. Re-attach them to the right holding or discard them.",
      unnamed: "Unnamed holding",
      detachedOn: "detached {{date}}",
      reattach: "Re-attach",
      discard: "Discard",
      pickAsset: "Choose a holding",
      confirmAttach: "Attach",
      attached: "Income terms re-attached",
      attachFailed: "Could not re-attach the income terms.",
      discardTitle: "Discard these income terms?",
      discardConfirm: "They will be deleted permanently.",
      discarded: "Income terms discarded",
      discardFailed: "Could not discard the income terms.",
    },
  },
  incomeTerms: {
    faceValueHint_one: "{{units}} unit held · {{perUnit}} per unit",
    faceValueHint_other: "{{units}} units held · {{perUnit}} per unit",
    pickDate: "Pick a date",
    clearDate: "Clear date",
    action: "Income terms",
    actionConfigured: "Income terms (configured)",
    title: "Income terms — {{name}}",
    addStreamTitle: "Add income stream",
    editStreamTitle: "Edit income stream",
    assetDescription: "Tell Bliss what this holding pays so it can project your passive income. Amounts are gross.",
    streamDescription: "A recurring income that isn't tied to an asset, like an allowance or a state pension.",
    notIncomeCapable: "This holding can't produce passive income.",
    autoTitle: "Automatic dividend data",
    autoSummary: "{{count}} payments in the last 12 months ({{frequency}}) · {{amount}} per share · last ex-date {{date}}",
    autoNone: "No dividends in the last 12 months (e.g. an accumulating fund). It projects zero and isn't flagged as missing.",
    autoUnavailable: "No reliable dividend data yet. Add an override below.",
    override: "Override automatic dividends",
    notDistributing: "Doesn't distribute income",
    groupTitle_one: "Income terms — {{name}} ({{count}} account)",
    groupTitle_other: "Income terms — {{name}} ({{count}} accounts)",
    applyToAllHoldings_one: "Apply to all holdings of {{symbol}} ({{count}})",
    applyToAllHoldings_other: "Apply to all {{count}} holdings of {{symbol}}",
    appliesToHoldings_one: "Applies to {{count}} holding of {{symbol}}",
    appliesToHoldings_other: "Applies to {{count}} holdings of {{symbol}}",
    mixedTitle: "These holdings don't share the same terms",
    mixedDescription: "Each account of {{symbol}} currently has its own income data. Pick an account to start from.",
    mixedAccount: "Account",
    mixedUseSame: "Use the same terms for all",
    mixedEditOne: "Edit this account only",
    removeFromAllConfirm_one: "Income terms will be removed from {{count}} holding of {{symbol}}. Stocks and ETFs fall back to automatic data.",
    removeFromAllConfirm_other: "Income terms will be removed from all {{count}} holdings of {{symbol}}. Stocks and ETFs fall back to automatic data.",
    faceValuePerUnitGroupHint_one: "{{units}} unit across {{count}} holding · {{total}} in total",
    faceValuePerUnitGroupHint_other: "{{units}} units across {{count}} holdings · {{total}} in total",
    select: "Select…",
    none: "None",
    otherIndex: "Other",
    namePlaceholder: "e.g. State pension",
    netRentHint: "Enter the rent you actually keep after costs and vacancy.",
    previewAnnual: "≈ {{amount}} / year",
    previewNext: "next payment {{date}}",
    previewEnds: "ends {{year}}",
    removeTerms: "Remove terms",
    deleteStream: "Delete stream",
    saved: "Income terms saved",
    deleted: "Income terms removed",
    saveFailed: "Could not save the income terms.",
    issuer: {
      GOVERNMENT: "Government",
      CORPORATE: "Corporate",
    },
    fields: {
      faceValueTotal: "Total face value",
      incomeType: "Income type",
      issuerType: "Issuer",
      faceValuePerUnit: "Face value per unit",
      couponRate: "Coupon rate",
      realCoupon: "Real coupon",
      referenceIndex: "Reference index",
      assumedIndexRate: "Assumed index rate",
      spread: "Spread",
      frequency: "Frequency",
      maturityDate: "Maturity date",
      anchorPaymentDate: "A payment date",
      monthlyRent: "Net monthly rent",
      annualIndexationPct: "Yearly indexation",
      leaseStart: "Lease start",
      leaseEndDate: "Lease end",
      apyPct: "APY",
      yieldPct: "Yield",
      orYieldPct: "…or yield",
      dividendPerUnit: "Annual dividend per share",
      name: "Name",
      category: "Category",
      amountPerPayment: "Amount per payment",
      currency: "Currency",
      startDate: "Start date",
      endDate: "End date",
    },
    errors: {
      required: "Required",
      invalid: "Invalid value",
      number: "Enter a number",
      negative: "Can't be negative",
      date: "Enter a valid date",
      endBeforeStart: "Must be after the start date",
      currency: "Use a 3-letter code (e.g. EUR)",
    },
  },
  maintenance: {
    fullRebuildSecuritiesStep: "It also refreshes securities data for any of your stocks and ETFs with missing or stale data.",
    securityData: {
      title: "Refresh my securities data",
      description: "Re-fetches profile, dividends and quotes for every stock and ETF you hold (earnings for stocks only). Use when a dividend schedule or Equity Analysis data looks wrong for your holdings.",
      button: "Refresh securities",
    },
    globalFundamentals: {
      title: "Refresh fundamentals for all symbols (global)",
      description: "Refreshes the shared SecurityMaster table for every symbol any tenant holds — not just yours. Heavier than refreshing your own securities; use after a data-provider incident.",
    },
  },

  // Manage Assets page (#81)
  manageAssets: {
    title: "Manage Assets",
    subtitle: "Every asset and liability in one place: prices, income terms, debt terms and asset classes.",
    loadFailed: "Failed to load assets. Please try again.",
    assetClassHint: "Applies to every holding of {{symbol}}. Choose Automatic to remove the override.",
    searchPlaceholder: "Search by symbol or name",
    empty: "No assets match these filters.",
    count: "{{count}} assets",
    count_one: "{{count}} asset",
    count_other: "{{count}} assets",
    loadMore: "Load more",
    loadingMore: "Loading…",
    columns: {
      assetClass: "Asset class",
    },
    filters: {
      type: "Type",
      allTypes: "All types",
      account: "Account",
      allAccounts: "All accounts",
      assetClass: "Asset class",
      allAssetClasses: "All asset classes",
      includeClosed: "Show closed positions",
      clear: "Clear filters",
    },
    status: {
      debtTermsMissing: "Debt terms missing",
      label: "Filter by status",
      stale: "Price stale",
      incomeMissing: "Income terms missing",
      dividendOverride: "Dividend override",
      lotMismatch: "Lot mismatch",
      assetClassOverridden: "Asset class overridden",
    },
    chips: {
      noPrice: "No price yet",
    },
    actions: {
      addIncomeTerms: "Add income terms",
      addDebtTerms: "Add debt terms",
      menu: "Actions for {{symbol}}",
      price: "Update price",
      history: "Price history",
      assetClass: "Asset class",
      copyTrading: "Switch to Copy Trading",
    },
    attention: {
      title: "{{count}} items need your attention",
      title_one: "{{count}} item needs your attention",
      title_other: "{{count}} items need your attention",
      allClear: "All caught up",
      allClearHint: "Prices are current and every loan and income-producing holding has its terms.",
      stale: "Prices to update",
      debtTermsMissing: "Loans without terms",
      incomeMissing: "Missing income terms",
      lotMismatch: "Lot mismatches to review",
    },
    sort: {
      label: "Sort",
      attention: "Needs attention first",
      name: "A–Z",
    },
    detached: {
      title: "{{count}} sets of income terms are detached",
      title_one: "{{count}} set of income terms is detached",
      title_other: "{{count}} sets of income terms are detached",
      description: "A portfolio rebuild couldn't match them to a holding. Re-attach or discard them.",
      review: "Review",
      hide: "Hide",
    },
  },

  // Processing status (#100): header chip, page banners, Settings → Processing
  activity: {
    types: {
      PORTFOLIO_UPDATE: "Updating portfolio",
      ANALYTICS_UPDATE: "Updating analytics",
      BANK_SYNC: "Syncing bank",
      IMPORT: "Importing transactions",
      SECURITY_DATA: "Refreshing market data",
      SUBSCRIPTION_SCAN: "Scanning subscriptions",
      INSIGHTS: "Generating insights",
    },
    stages: {
      scheduling: "Scheduling",
      recalculating_lots: "Recalculating lots",
      updating_cash: "Updating cash",
      valuing_assets: "Valuing assets",
      updating_debts: "Updating debts",
      updating_analytics: "Updating analytics",
      fetching_bank: "Fetching bank transactions",
      syncing_copies: "Syncing eToro copies",
      classifying: "Classifying transactions",
      processing_file: "Processing file",
      committing: "Saving transactions",
      refreshing_market_data: "Refreshing market data",
      scanning: "Scanning",
      generating_insights: "Generating insights",
    },
    triggers: {
      user_change: "Your change",
      bank_sync: "Bank sync",
      import: "Import",
      nightly: "Nightly",
      manual_rebuild: "Rebuild",
      agent: "AI agent",
      auto_refresh: "Automatic refresh",
    },
    states: {
      queued: "Queued",
      running: "Updating",
      stalled: "Taking longer than usual",
      completed: "Completed",
      failed: "Failed",
    },
    chip: {
      queued: "Queued",
      running: "Updating",
      stalled: "Taking longer than usual",
      failed: "Update failed",
      ariaLabel: "Background processing status",
      title: "Background processing",
      empty: "Everything is up to date",
      lastUpdated: "Last updated {{time}}",
      jobs: "{{count}} jobs",
      workerOffline: "The background worker is offline. Queued work will start when it is back.",
      failedHint: "Error {{code}}. Admins can see details in Settings → Processing.",
      pending: "Starting…",
    },
    banner: {
      queued: "Numbers on this page will be recalculated shortly ({{label}}). They'll refresh automatically.",
      running: "Numbers on this page are being recalculated ({{label}}). They'll refresh automatically.",
      runningProgress: "Numbers on this page are being recalculated ({{label}}, {{progress}}%). They'll refresh automatically.",
      stalled: "Recalculating is taking longer than usual ({{label}}). Numbers may be out of date.",
      failed: "The last update of this page's data failed ({{label}}). Numbers may be out of date.",
      updated: "Updated {{time}}",
    },
    time: {
      justNow: "just now",
      underSecond: "< 1 s",
      minutesAgo: "{{count}} min ago",
      hoursAgo: "{{count}} h ago",
      daysAgo: "{{count}} d ago",
      seconds: "{{count}} s",
      minutes: "{{count}} min",
      hours: "{{count}} h",
    },
    processing: {
      title: "Processing",
      description: "Background work on your data: what is running now, the last 24 hours, and admin rebuilds.",
      liveTitle: "Live",
      liveEmpty: "Nothing is running. Your data is up to date.",
      recentTitle: "Last 24 hours",
      recentEmpty: "No background work in the last 24 hours.",
      rebuildsTitle: "Recent rebuilds",
      unavailable: "Processing status is unavailable right now.",
      workerOffline: "The background worker is offline. Queued work will start when it is back.",
      elapsed: "running for {{time}}",
      queuedFor: "queued for {{time}}",
      took: "took {{time}}",
      finished: "finished {{time}}",
      errorCode: "Error {{code}}",
      steps_one: "{{count}} step",
      steps_other: "{{count}} steps",
    },
    notification: {
      failed_one: "{{count}} background update failed",
      failed_other: "{{count}} background updates failed",
    },
    maintenance: {
      seeProgress: "See progress",
      seeProgressHint: "Live progress and the rebuild history are in Processing.",
    },
  },

  // Enable Banking (#132)
  // eToro copy trading (#143)
  etoro: {
    section: {
      title: "eToro copy trading",
      description: "Follow the value and the holdings of your eToro copy trades. Bliss reads them with a Read key from your own eToro account. It never trades and never creates transactions.",
      connect: "Connect eToro",
      empty: "No eToro account connected yet.",
      guide: "How to create a Read key",
      loadFailed: "Couldn't load your eToro connections.",
    },
    status: {
      ACTIVE: "Connected",
      NEEDS_RECONNECT: "Needs a new key",
    },
    connection: {
      account: "Bliss account",
      noAccount: "No account",
      key: "Key",
      lastSync: "Last sync",
      never: "Never",
      lastSyncFailed: "Last sync failed ({{code}})",
      syncNow: "Sync now",
      syncStarted: "Sync started. Values update in a few minutes.",
      cooldown: "Synced recently. Try again in {{minutes}} min.",
      replaceKey: "Replace key",
      remove: "Remove",
      removeTitle: "Remove this eToro connection?",
      removeDescription: "Bliss stops updating your copies. Your Copy Trading holdings and their history stay.",
      removeConfirm: "Remove",
      removed: "eToro connection removed",
      copies: "Copies",
      noCopies: "No copy trades on this account.",
    },
    copy: {
      closed: "Closed on eToro",
      linkedTo: "Linked to {{symbol}}",
      notLinked: "Not linked",
      notLinkedHint: "Record this copy as a Copy Trading holding with the ticker {{username}} in this account, or switch an existing fund to Copy Trading in Manage Assets.",
      startedAt: "Since {{date}}",
    },
    dialog: {
      title: "Connect eToro",
      replaceTitle: "Replace eToro key",
      description: "Create a Read key in eToro (Settings → Trading → API Key Management) and paste both values. Bliss refuses keys that can trade.",
      apiKey: "API key",
      userKey: "User key",
      userKeyHint: "Stored encrypted and never shown again.",
      account: "Bliss account",
      accountHint: "The account where you record your eToro copies.",
      accountPlaceholder: "Choose an account",
      submit: "Connect",
      replaceSubmit: "Save key",
      checking: "Checking with eToro…",
      successTitle: "Connected as {{username}}",
      successCopies_one: "{{count}} copy trade found",
      successCopies_other: "{{count}} copy trades found",
      noCopies: "No copy trades on this account yet.",
      done: "Done",
      cancel: "Cancel",
    },
    errors: {
      ETORO_WRITE_SCOPE: "This key can place trades. Create a Read key in eToro and use that one.",
      ETORO_KEY_INVALID: "eToro didn't accept this key. Check both values and that the key hasn't expired.",
      ETORO_UNAVAILABLE: "eToro couldn't be reached. Try again later.",
      ETORO_RATE_LIMITED: "eToro is limiting requests. Try again in a minute.",
      ETORO_ALREADY_CONNECTED: "This eToro account is already connected.",
      ETORO_DIFFERENT_LOGIN: "This key belongs to a different eToro account.",
      KEYS_REQUIRED: "Enter both keys.",
      ACCOUNT_REQUIRED: "Choose a Bliss account.",
      generic: "Something went wrong. Try again.",
    },
    badge: {
      linked: "Linked to eToro",
      needsReconnect: "eToro key needs replacing",
      closed: "Copy closed on eToro",
      notLinked: "Not linked to eToro",
      lastValue: "eToro value from {{date}}",
    },
    convert: {
      title: "Switch to Copy Trading",
      description: "Move {{symbol}} to the Copy Trading category. Its transactions, income terms and history stay as they are. Once your eToro account is connected, Bliss follows the copy's value and holdings.",
      username: "eToro username of the investor you copy",
      usernamePlaceholder: "@investor",
      pickCopy: "Choose the copy",
      otherInvestor: "Another investor",
      matched: "matches the name",
      otherAccount: "Your eToro connection uses the account {{account}}. This holding is in another account, so it won't be linked after switching: move its transactions to {{account}} first.",
      submit: "Switch",
      success: "{{symbol}} is now a Copy Trading holding",
      errors: {
        INVALID_USERNAME: "Enter a valid eToro username.",
        COPY_HOLDING_EXISTS: "This account already has a holding for that investor.",
        COPY_TRADING_CATEGORY_MISSING: "The Copy Trading category is missing. Ask your Bliss admin to add it.",
        generic: "Couldn't switch this holding. Try again.",
      },
    },
  },
  enableBanking: {
    connect: {
      button: "Connect EU bank (Enable Banking)",
      title: "Connect a European bank",
      description: "Choose your bank. You'll be sent to its website to approve read-only access through Enable Banking.",
      search: "Search banks",
      loading: "Loading banks...",
      loadFailed: "Could not load the bank list. Try again later.",
      noBanks: "No banks found for your workspace's countries.",
      startDate: "Transactions will be imported from",
      consentDays_one: "Access lasts up to {{count}} day, then you'll be asked to reconnect.",
      consentDays_other: "Access lasts up to {{count}} days, then you'll be asked to reconnect.",
      continue: "Continue to bank",
      redirecting: "Redirecting...",
      errorCountryNotEnabled: "This bank's country isn't enabled for your workspace.",
      errorUnknownBank: "This bank is no longer available. Pick another one.",
      errorGeneric: "Could not start the bank connection. Try again later.",
    },
    callback: {
      reconnectedTitle: "Reconnected — syncing",
      reconnectedDesc: "Your bank access was renewed and a sync has started.",
      errorTitle: "Bank connection failed",
      invalid_state: "The connection link expired or was already used. Start again from Accounts.",
      denied: "Access was not granted at your bank. Nothing was connected.",
      exchange_failed: "Your bank didn't confirm the connection. Try again in a few minutes.",
      not_enabled: "Enable Banking isn't set up for your workspace. An admin can add it in Settings → Bank sync.",
      unknown: "Something went wrong while connecting your bank.",
    },
    mapping: {
      title: "Link your bank accounts",
      description: "Choose which Bliss account each {{bank}} account syncs into.",
      loading: "Loading accounts...",
      loadFailed: "Could not load this connection. Try again later.",
      unnamed: "Bank account",
      balance: "Balance {{amount}}",
      syncInto: "Sync into",
      existingAccounts: "Existing accounts",
      createNew: "Create new account",
      dontSync: "Don't sync",
      linkedPlaid: "synced via Plaid",
      linkedEnableBanking: "synced via another connection",
      switchHint: "This account is synced by another connection. Linking it here moves its sync to this bank; its history is kept.",
      conflict: "Already synced through another person's connection — authorise a joint account under one holder only.",
      newName: "Account name",
      newBank: "Bank",
      newCountry: "Country",
      newCurrency: "Currency",
      createBankOption: "Add \"{{name}}\"",
      noAccounts: "Your bank returned no accounts.",
      submit: "Link accounts",
      submitting: "Linking...",
      successTitle: "Accounts linked — syncing",
      successDesc_one: "{{count}} account will sync. The first sync has started.",
      successDesc_other: "{{count}} accounts will sync. The first sync has started.",
      successNoneDesc: "No accounts were linked. You can link them later from this connection.",
      errorAlreadyConnected: "One of these bank accounts is already synced through another connection.",
      errorDuplicate: "Two bank accounts can't sync into the same Bliss account.",
      errorCreateIncomplete: "Fill in the name, bank, country and currency of each new account.",
      errorGeneric: "Could not link the accounts. Try again.",
      switchTitle: "Switch the sync provider?",
      switchDesc: "These accounts will stop syncing from their current provider and sync through Enable Banking instead. Their existing history is kept.",
      switchFrom: "Currently synced via {{provider}} ({{bank}})",
      switchConfirm: "Switch and link",
      providerPlaid: "Plaid",
      providerEnableBanking: "Enable Banking",
    },
    card: {
      title: "Enable Banking connection",
      statusActive: "Active",
      statusExpiring: "Expiring soon",
      statusExpired: "Expired",
      statusError: "Error",
      statusDisconnected: "Disconnected",
      statusNeedsMapping: "Needs mapping",
      expiringDesc: "Bank access ends soon. Reconnect to keep syncing.",
      expiredDesc: "Bank access has ended. Reconnect to resume syncing.",
      errorDesc: "The last sync failed. Try again later or reconnect.",
      needsMappingDesc: "Choose which Bliss accounts this bank feeds before it can sync.",
      bank: "Bank",
      accessExpires: "Access expires",
      expiresIn_one: "{{date}} ({{count}} day left)",
      expiresIn_other: "{{date}} ({{count}} days left)",
      lastSync: "Last sync",
      balanceAsOf: "as of {{date}}",
      syncNow: "Sync now",
      syncStarted: "Sync started",
      syncStartedDesc: "New transactions will appear shortly.",
      syncFailed: "Failed to trigger sync",
      syncNotActive: "Reconnect this bank before syncing.",
      syncNoAccounts: "Link at least one account before syncing.",
      budgetTitle: "Daily refresh limit reached",
      budgetDesc: "Your bank allows only a few refreshes per day. You can sync again after {{time}}.",
      budgetDescNoTime: "Your bank allows only a few refreshes per day. Try again tomorrow.",
      reconnect: "Reconnect",
      reconnectOnlyAuthoriser: "Only the person who authorised this connection can reconnect it.",
      reconnectFailed: "Could not start the reconnection. Try again later.",
      mapAccounts: "Map accounts",
      manageAccounts: "Manage accounts",
      disconnect: "Disconnect",
      disconnecting: "Disconnecting...",
      disconnectOnlyAdmin: "Only an admin or the person who authorised this connection can disconnect it.",
      disconnectTitle: "Disconnect {{bank}}?",
      disconnectDesc: "Syncing stops and the bank access is ended. Your accounts and their transaction history are kept.",
      disconnected: "Bank disconnected",
      disconnectedDesc: "Syncing has stopped. Your accounts and history are kept.",
      disconnectFailed: "Failed to disconnect",
      statusPaused: "Paused",
      pausedDesc: "Paused: credentials missing.",
      pausedAdminLink: "Set up Bank sync",
      pausedAskAdmin: "Ask an admin to add the Enable Banking credentials.",
    },
    pending: {
      message: "{{bank}} is connected, but no account is linked yet. Choose which accounts to sync.",
    },
    detail: {
      badge: "Enable Banking",
      deleteBlockedHint: "Disconnect this bank connection first (use \"Disconnect\" above) before deleting the account.",
    },
    credentials: {
      title: "Enable Banking (EU banks)",
      intro: "Sync EU bank accounts automatically through your own Enable Banking application. A free application can link up to 5 accounts.",
      guideLink: "Read the setup guide",
      callbackLabel: "Callback URL",
      callbackHint: "Register this URL as a redirect URL on your Enable Banking application.",
      copy: "Copy",
      copied: "Copied",
      appIdLabel: "Application id",
      appIdInvalid: "The application id is a UUID, like 6f0c1a2b-3c4d-4e5f-8a9b-0c1d2e3f4a5b.",
      keyLabel: "Private key",
      save: "Save and verify",
      saving: "Verifying...",
      cancel: "Cancel",
      statusVERIFIED: "Verified",
      statusUNVERIFIED: "Not verified",
      statusINVALID: "Invalid",
      unverifiedDesc: "Enable Banking couldn't be reached to check these credentials. They are saved; use Re-verify to check again.",
      invalidDesc: "Enable Banking rejected these credentials. Scheduled syncs are paused until you re-verify or replace them.",
      callbackMissing: "The callback URL isn't registered on this application yet. Bank connections will fail until it is.",
      appId: "Application id",
      fingerprint: "Key fingerprint",
      keyBits: "{{bits}}-bit RSA",
      lastValidated: "Last verified",
      never: "Never",
      linkedAccounts: "Linked accounts",
      linkedAccountsValue: "{{used}} of {{limit}}",
      activeConnections: "Active connections",
      replace: "Replace credentials",
      reverify: "Re-verify",
      remove: "Remove",
      replaceHint: "To rotate only the key, keep the application id and upload the new file.",
      savedTitle: "Enable Banking is set up",
      savedDesc: "Your workspace can now connect EU banks from Accounts.",
      savedUnverifiedDesc: "Saved, but Enable Banking couldn't be reached to verify them. Use Re-verify later.",
      verifiedTitle: "Credentials verified",
      stillInvalidTitle: "Enable Banking still rejects these credentials",
      stillUnverifiedTitle: "Enable Banking couldn't be reached",
      removedTitle: "Credentials removed",
      removedDesc: "Connections are paused until credentials are added again.",
      errorINVALID_CREDENTIALS: "Enable Banking rejected these credentials. Check that the private key belongs to this application.",
      errorSANDBOX_APP: "This is a sandbox application. Create a production application in restricted mode.",
      errorINVALID_APP_ID: "The application id is a UUID, like 6f0c1a2b-3c4d-4e5f-8a9b-0c1d2e3f4a5b.",
      errorGeneric: "Could not save the credentials. Try again.",
      loadFailed: "Could not load the Enable Banking settings.",
      confirmAppChangeTitle: "Change the Enable Banking application?",
      confirmAppChangeDesc_one: "{{count}} connection will stop syncing until it is reconnected. Its accounts and history are kept.",
      confirmAppChangeDesc_other: "{{count}} connections will stop syncing until they are reconnected. Their accounts and history are kept.",
      confirmRemoveTitle: "Remove the Enable Banking credentials?",
      confirmRemoveDesc_one: "{{count}} connection will be paused until credentials are added again. Accounts, mappings and transactions are kept.",
      confirmRemoveDesc_other: "{{count}} connections will be paused until credentials are added again. Accounts, mappings and transactions are kept.",
      confirmContinue: "Continue",
      pausedBanner_one: "{{count}} connection is paused until you add your Enable Banking credentials.",
      pausedBanner_other: "{{count}} connections are paused until you add your Enable Banking credentials.",
      historyTitle: "Change history",
      historyEmpty: "No changes yet.",
      historySET: "Set",
      historyREPLACED: "Replaced",
      historyKEY_ROTATED: "Key rotated",
      historyREMOVED: "Removed",
      historyBy: "by {{name}}",
      historyUnknownUser: "a former member",
      notification: "Enable Banking credentials need attention",
      setupLink: "Set up EU bank sync",
    },
    keyInput: {
      drop: "Drop the .pem file from Enable Banking here",
      choose: "Choose file",
      pasteInstead: "Paste instead",
      uploadInstead: "Upload a file instead",
      pasteLabel: "Private key (PEM)",
      valid: "Valid RSA private key · {{bits}}-bit",
      clear: "Clear",
      readFailed: "Could not read this file.",
      errorKEY_NOT_PEM: "This isn't a PEM private key.",
      errorKEY_PUBLIC: "This is a public key. Use the private key (.pem) Enable Banking gave you.",
      errorKEY_CERTIFICATE: "This is a certificate, not a private key.",
      errorKEY_NOT_RSA: "Only RSA private keys are supported.",
      errorKEY_ENCRYPTED: "Password-protected keys aren't supported. Use the unencrypted .pem from Enable Banking.",
      errorKEY_TRUNCATED: "The key looks incomplete: the END line is missing or the text was cut.",
      errorKEY_TOO_LARGE: "This is larger than 16 KB, too big for a private key.",
      errorKEY_TOO_SMALL: "RSA keys must be at least 2048 bits.",
    },
    settings: {
      historyDaysHint: "Also applies to Enable Banking connections.",
    },
  },
  // Installable PWA (#155)
  pwa: {
    install: {
      button: "Install app",
      settingsTitle: "Bliss app",
      settingsDescription: "Install Bliss to open it in its own window, from your home screen, dock or taskbar.",
      bannerTitle: "Install the Bliss app",
      bannerDescription: "Open Bliss in its own window, from your home screen, dock or taskbar.",
      dismiss: "Dismiss",
      iosTitle: "Add Bliss to your Home Screen",
      iosStep1: "Tap the Share button in the browser toolbar.",
      iosStep2: "Scroll down and tap “Add to Home Screen”.",
      iosStep3: "Tap “Add”. Bliss appears on your Home Screen.",
      iosSignInNote: "The app doesn't share your browser's login, so you sign in once inside the app.",
      done: "Got it",
    },
    update: {
      reload: "Reload",
    },
    banks: {
      desktopOnly: "Connect and reconnect banks from a desktop browser. Synced transactions can still be reviewed here.",
    },
    offline: {
      title: "You're offline",
      description: "Bliss needs a connection to show your data. Your figures load again as soon as you're back online.",
      retry: "Try again",
    },
  },
  // Onboarding wizard (#154): every step, namespaced
  onboarding: {
    welcome: {
      title: "Welcome to",
      subtitle: "Let's personalize your experience",
      countries: "Your countries",
      currencies: "Your currencies",
    },
    connect: {
      title: "Connect your accounts",
      subtitle: "Link a bank for automatic sync, import a spreadsheet, or add accounts manually",
      bankTitle: "Connect your bank",
      bankDescription: "Choose the provider that covers your bank. You can add the other one later.",
      learnMore: "Learn more",
      plaid: {
        title: "Plaid — US, Canada & UK banks",
        description: "Securely link your bank accounts via Plaid for automatic transaction sync.",
        button: "Connect bank account",
        notConfigured: "Plaid isn't set up on this server.",
        notConfiguredHint: "The server administrator adds the Plaid keys; the guide explains how.",
        error: "Couldn't reach Plaid, try again later.",
      },
      enableBanking: {
        title: "Enable Banking — EU banks",
        description: "Link EU banks through PSD2 consent, using your own Enable Banking application.",
        connect: "Connect EU bank",
        setup: "Set up EU bank sync",
        adminOnly: "A workspace admin can set up EU bank sync in Settings.",
      },
      setup: {
        title: "Set up EU bank sync",
        description: "Bliss connects to EU banks through your own Enable Banking application. Add its ID and private key: Bliss checks them before saving, then you pick your bank.",
        needTitle: "What you need",
        needAccount: "An Enable Banking account",
        needApp: "A production application in restricted mode",
        needCallback: "The callback URL below registered on that application",
        limit: "Restricted mode links up to 5 of your own bank accounts.",
        guide: "Step-by-step guide",
      },
      importTitle: "Import a spreadsheet",
      importDescription: "Upload a CSV or Excel file with your transactions.",
    },
    accounts: {
      title: "Add your banks & accounts",
      subtitle: "Pick the banks you use and add an account for each. You can fill in the account numbers later.",
      selectBanks: "Select your banks",
      addDifferentBank: "Add a different bank",
      bankName: "Bank name",
      account: "Account",
      addAccount: "Add account",
      removeAccount: "Remove account",
      removeBank: "Remove bank",
      bankCap: "You can add up to {{count}} banks here — you can add more later.",
      pickCountry: "Pick the country for each account — it defaults to {{country}}.",
      createdIn: "Accounts will be created in {{country}}.",
      skip: "Skip for now",
      create_one: "Create {{count}} account",
      create_other: "Create {{count}} accounts",
    },
    errors: {
      noTenant: "No tenant found",
      tenantMissing: "Tenant name or plan missing. Please contact support.",
      savePreferences: "Could not save your preferences. Please try again.",
      noUser: "Could not identify your user. Please try again.",
      setupAccounts: "Could not set up your accounts. Please try again.",
    },
  },
};

export default en;